import React, { useEffect, useMemo, useRef, useState } from 'react';
import aiService from '../services/aiService';
import { getPlaceDetails, lookupBusinessByName } from '../services/businessAPI';
import twilioVoiceService, { ManualCallState } from '../services/twilioVoice';
import {
  addProspectToCallSheet,
  createCallSheet,
  getDueProspectReminders,
  getOrCreateCallSheetForWeek,
  getOrCreateTodayCallSheet,
  listOutboundCallsForSheet,
  getProspect,
  getProspectCallLogs,
  getProspectContacts,
  listCallSheetItems,
  listCallSheets,
  removeSavedProspectByUserAndBusiness,
  removeCallSheetItem,
  saveCallLog,
  setProspectSaved,
  subscribeCallSheetItems,
  subscribeOutboundCallsForSheet,
  updateCallSheetItemState,
  upsertProspect
} from '../services/firebase';
import { auth } from '../services/firebase';
import type { CallSheetItemRecord, CallSheetRecord, OutboundCallRecord, ProspectContactRecord, ProspectRecord } from '../types/prospecting';
import type { OrganizationKey } from '../types/organizations';
import { orgStorageKey, STORAGE_KEYS } from '../utils/orgScope';
import type { AppointmentType } from '../types/prospecting';
import { formatDateKey, formatDateKeyMdy, formatMdyTime, formatWeekOfLabel, isLikelyGooglePlaceId, nowIso, startOfWeekMonday } from '../types/prospecting';
import ProspectDetailModal from './ProspectDetailModal';

type AppUser = { id: string; email: string; name: string; role: 'admin' | 'user' | 'SuperAdmin'; createdAt: string };

type ProspectSnapshot = {
  id: string;
  name: string;
  category: string;
  address: string;
  city?: string;
  state?: string;
  zip?: string;
  phone?: string;
  website?: string;
  lat?: number;
  lng?: number;
};

const OUTCOMES = ['Called', 'Call Back', 'Rejected', 'New Appointment', 'F/U Appointment', 'DM Spoken To'] as const;
const APPOINTMENT_OUTCOMES = new Set(['New Appointment', 'F/U Appointment']);
type QueueState = 'uncalled' | 'called' | 'callback' | 'rejected' | 'appointment';
const QUALITY_LABELS: Record<1 | 2 | 3 | 4 | 5, string> = {
  1: 'Very Low',
  2: 'Low',
  3: 'Medium',
  4: 'High',
  5: 'Premium'
};

const queueStateFromOutcome = (outcome?: string): QueueState => {
  if (!outcome) return 'uncalled';
  const normalized = outcome.toLowerCase().trim();
  if (normalized === 'call back' || normalized === 'follow_up') return 'callback';
  if (normalized === 'rejected' || normalized === 'dnc' || normalized === 'wrong_number') return 'rejected';
  if (
    normalized === 'new appointment' ||
    normalized === 'f/u appointment' ||
    normalized === 'booked'
  ) {
    return 'appointment';
  }
  if (normalized === 'uncalled') return 'uncalled';
  return 'called';
};

const callSheetOutcomeFromOutbound = (outcome?: string): string | undefined => {
  if (!outcome) return undefined;
  if (outcome === 'booked') return 'New Appointment';
  if (outcome === 'follow_up') return 'Call Back';
  if (outcome === 'rejected' || outcome === 'dnc' || outcome === 'wrong_number') return 'Rejected';
  if (outcome === 'dm_reached') return 'DM Spoken To';
  if (outcome === 'called') return 'Called';
  if (outcome === 'failed') return undefined;
  return 'Called';
};

type AppointmentDraft = {
  prospectId: string;
  outcome: string;
  date: string;
  time: string;
  durationMinutes: number;
  appointmentType: AppointmentType;
  appointmentLocation: string;
  appointmentContactName: string;
};

type QueueSequenceState = {
  active: boolean;
  stopRequested: boolean;
  sheetId: string;
  startedAt: string;
  total: number;
  completed: number;
  failed: number;
  skipped: number;
  currentProspectId: string | null;
  lastError: string | null;
};

const ACTIVE_OUTBOUND_STATUSES = new Set<NonNullable<OutboundCallRecord['status']>>([
  'queued',
  'dialing',
  'in_progress'
]);

const isActiveOutboundStatus = (status?: OutboundCallRecord['status']): boolean =>
  !!status && ACTIVE_OUTBOUND_STATUSES.has(status);

const sleep = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms));

const websiteDisplayText = (raw?: string): string => {
  if (!raw) return 'Website N/A';
  try {
    const withScheme = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
    const u = new URL(withScheme);
    const host = u.hostname.replace(/^www\./i, '');
    const path = u.pathname && u.pathname !== '/' ? u.pathname : '';
    const base = `${host}${path}`;
    const trimmed = base.length > 52 ? `${base.slice(0, 49)}...` : base;
    return trimmed;
  } catch {
    const noQuery = raw.split('?')[0].split('#')[0];
    return noQuery.length > 52 ? `${noQuery.slice(0, 49)}...` : noQuery;
  }
};

const clamp = (n: number, min: number, max: number): number => Math.min(max, Math.max(min, n));

const roundToQuarter = (d: Date): Date => {
  const copy = new Date(d);
  const minutes = copy.getMinutes();
  const rounded = Math.round(minutes / 15) * 15;
  copy.setMinutes(rounded, 0, 0);
  return copy;
};

const splitTime = (time: string): { hour: number; minute: number } => {
  const [hourRaw, minuteRaw] = (time || '09:00').split(':');
  const hour = Number(hourRaw);
  const minute = Number(minuteRaw);
  return {
    hour: Number.isFinite(hour) ? clamp(hour, 7, 21) : 9,
    minute: [0, 15, 30, 45].includes(minute) ? minute : 0
  };
};

const toTimeString = (hour: number, minute: number): string => {
  return `${String(clamp(hour, 7, 21)).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
};

const parseDateKeyAsLocal = (dateKey: string): Date => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateKey);
  if (!m) return new Date(dateKey);
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12, 0, 0, 0);
};

const isAutoCallSheet = (sheet: CallSheetRecord): boolean => {
  const name = sheet.name.trim().toLowerCase();
  return name === 'today' || name.startsWith('week of ');
};

const formatSheetDisplayName = (sheet: CallSheetRecord, todayKey: string, currentWeekStartKey: string): string => {
  const dateLabel = formatDateKeyMdy(sheet.dateKey);
  const name = sheet.name.trim();
  if (/^week of /i.test(name)) {
    return sheet.dateKey === currentWeekStartKey ? `Current Week - ${dateLabel}` : `Week of ${dateLabel}`;
  }
  if (/^today$/i.test(name)) {
    if (sheet.dateKey === todayKey) return `Today - ${dateLabel}`;
    const weekday = parseDateKeyAsLocal(sheet.dateKey).toLocaleDateString('en-US', { weekday: 'short' });
    return `${weekday} - ${dateLabel}`;
  }
  return `${sheet.name} - ${dateLabel}`;
};

const toErrorText = (value: unknown): string | null => {
  if (value == null) return null;
  if (typeof value === 'string') {
    const s = value.trim();
    if (!s) return null;
    if (s === '[object Object]' || s.toLowerCase() === 'object object') return null;
    return s;
  }
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) {
    for (const entry of value) {
      const text = toErrorText(entry);
      if (text) return text;
    }
    return null;
  }
  if (typeof value === 'object') {
    const objectValue = value as Record<string, unknown>;
    const keys = ['detail', 'message', 'error', 'reason', 'title', 'description'];
    for (const key of keys) {
      const text = toErrorText(objectValue[key]);
      if (text) return text;
    }
    try {
      return JSON.stringify(objectValue);
    } catch {
      return null;
    }
  }
  return null;
};

type ProspectDetailSyncResult = {
  label: string;
  color: string;
  background: string;
};

const summarizeDetailSyncResult = (result: {
  phoneImproved: boolean;
  websiteImproved: boolean;
  addressImproved: boolean;
  placeIdRecovered: boolean;
  meaningfulImprovement: boolean;
}): ProspectDetailSyncResult => {
  if (result.phoneImproved && result.websiteImproved) {
    return { label: 'Phone + website recovered', color: '#065f46', background: '#dcfce7' };
  }
  if (result.phoneImproved) {
    return { label: 'Phone recovered', color: '#065f46', background: '#dcfce7' };
  }
  if (result.websiteImproved) {
    return { label: 'Website recovered', color: '#1d4ed8', background: '#dbeafe' };
  }
  if (result.addressImproved && result.placeIdRecovered) {
    return { label: 'Address + place match improved', color: '#9a3412', background: '#ffedd5' };
  }
  if (result.addressImproved) {
    return { label: 'Address improved only', color: '#9a3412', background: '#ffedd5' };
  }
  if (result.placeIdRecovered) {
    return { label: 'Place match improved only', color: '#9a3412', background: '#ffedd5' };
  }
  return { label: 'No new phone or website found', color: '#6b7280', background: '#f3f4f6' };
};

export default function CallSheetView(props: {
  currentUser: AppUser;
  orgKey: OrganizationKey;
  savedProspects: ProspectSnapshot[];
  onRejectedRemoveSaved?: (candidateIds: string[]) => void | Promise<void>;
}) {
  const { currentUser, orgKey, savedProspects, onRejectedRemoveSaved } = props;
  const [sheets, setSheets] = useState<CallSheetRecord[]>([]);
  const [activeSheet, setActiveSheet] = useState<CallSheetRecord | null>(null);
  const [items, setItems] = useState<CallSheetItemRecord[]>([]);
  const [prospectMap, setProspectMap] = useState<Record<string, ProspectRecord | null>>({});
  const [contactsMap, setContactsMap] = useState<Record<string, ProspectContactRecord[]>>({});
  const [loading, setLoading] = useState(false);
  const [openProspectId, setOpenProspectId] = useState<string | null>(null);
  const [quickAddQuery, setQuickAddQuery] = useState('');
  const [showAllQuickAdd, setShowAllQuickAdd] = useState(false);
  const [showQuickAddPanel, setShowQuickAddPanel] = useState(false);
  const [showSheetPicker, setShowSheetPicker] = useState(false);
  const [sheetSearchQuery, setSheetSearchQuery] = useState('');
  const [showAllSheets, setShowAllSheets] = useState(false);
  const [addingByStableId, setAddingByStableId] = useState<Record<string, boolean>>({});
  const [syncingByProspect, setSyncingByProspect] = useState<Record<string, boolean>>({});
  const [detailSyncResultByProspect, setDetailSyncResultByProspect] = useState<Record<string, ProspectDetailSyncResult>>({});
  const [syncingMissingDetails, setSyncingMissingDetails] = useState(false);
  const [movingByItemId, setMovingByItemId] = useState<Record<string, boolean>>({});
  const [carryingForward, setCarryingForward] = useState(false);
  const [startingAiCallByProspect, setStartingAiCallByProspect] = useState<Record<string, boolean>>({});
  const [endingAiCallByProspect, setEndingAiCallByProspect] = useState<Record<string, boolean>>({});
  const [startingManualCallByProspect, setStartingManualCallByProspect] = useState<Record<string, boolean>>({});
  const [endingManualCallByProspect, setEndingManualCallByProspect] = useState<Record<string, boolean>>({});
  const [latestOutboundByProspect, setLatestOutboundByProspect] = useState<Record<string, OutboundCallRecord>>({});
  const latestOutboundRef = useRef<Record<string, OutboundCallRecord>>({});
  const itemsRef = useRef<CallSheetItemRecord[]>([]);
  const [rowFilter, setRowFilter] = useState<'all' | 'uncalled' | 'called' | 'callback' | 'appointment' | 'rejected'>('all');
  const [notesByProspect, setNotesByProspect] = useState<Record<string, string>>({});
  const [appointmentDraft, setAppointmentDraft] = useState<AppointmentDraft | null>(null);
  const [savingAppointment, setSavingAppointment] = useState(false);
  const [aiSequence, setAiSequence] = useState<QueueSequenceState | null>(null);
  const [manualSequence, setManualSequence] = useState<QueueSequenceState | null>(null);
  const [manualVoiceState, setManualVoiceState] = useState<ManualCallState>({
    status: 'idle',
    prospectId: null,
    callId: null,
    error: null
  });
  const [dueCallbacks, setDueCallbacks] = useState<ProspectRecord[]>([]);
  const [loadingDueCallbacks, setLoadingDueCallbacks] = useState(false);
  const [addingDueCallbackIds, setAddingDueCallbackIds] = useState<Record<string, boolean>>({});
  const [addingAllDueCallbacks, setAddingAllDueCallbacks] = useState(false);
  const [removingDueCallbackIds, setRemovingDueCallbackIds] = useState<Record<string, boolean>>({});
  const [removingByItemId, setRemovingByItemId] = useState<Record<string, boolean>>({});
  const rowRefs = useRef<Record<string, HTMLElement | null>>({});
  const aiSequenceRunIdRef = useRef(0);
  const aiSequenceStopRequestedRef = useRef(false);
  const manualSequenceRunIdRef = useRef(0);
  const manualSequenceStopRequestedRef = useRef(false);
  const [isCompact, setIsCompact] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < 1200;
  });

  const todayKey = useMemo(() => formatDateKey(new Date()), []);
  const currentWeekStartKey = useMemo(() => formatDateKey(startOfWeekMonday(new Date())), []);
  const activeSheetStorageKey = useMemo(
    () => orgStorageKey(STORAGE_KEYS.activeSheet, currentUser.id, orgKey),
    [currentUser.id, orgKey]
  );

  const refreshDueCallbacks = async () => {
    setLoadingDueCallbacks(true);
    try {
      const rows = await getDueProspectReminders(currentUser.id, undefined, orgKey);
      setDueCallbacks(rows.filter((row) => row.callbackPending));
    } finally {
      setLoadingDueCallbacks(false);
    }
  };

  const removeDueCallback = async (prospectId: string) => {
    const prospect = dueCallbacks.find((row) => row.id === prospectId);
    const label = prospect?.name || prospectId;
    const confirmed = window.confirm(`Remove "${label}" from callbacks due?`);
    if (!confirmed) return;
    setRemovingDueCallbackIds((prev) => ({ ...prev, [prospectId]: true }));
    try {
      await upsertProspect(currentUser.id, prospectId, {
        userId: currentUser.id,
        orgKey,
        callbackPending: false,
        nextReminderAt: null,
        nextActionType: 'none',
        updatedAt: nowIso()
      } as any).catch(() => undefined);
      setProspectMap((prev) => {
        const current = prev[prospectId];
        if (!current) return prev;
        return {
          ...prev,
          [prospectId]: {
            ...current,
            callbackPending: false,
            nextReminderAt: undefined,
            nextActionType: 'none',
            updatedAt: nowIso()
          }
        };
      });
      await refreshDueCallbacks().catch(() => undefined);
    } finally {
      setRemovingDueCallbackIds((prev) => {
        const next = { ...prev };
        delete next[prospectId];
        return next;
      });
    }
  };

  const hydrateProspect = async (prospectId: string, force = false) => {
    if (!force && prospectMap[prospectId] !== undefined) return;
    const p = await getProspect(prospectId);
    setProspectMap((prev) => ({ ...prev, [prospectId]: p }));
    const contacts = await getProspectContacts(currentUser.id, prospectId).catch(() => []);
    setContactsMap((prev) => ({ ...prev, [prospectId]: contacts }));
  };

  const backfillItemStatesFromHistory = async (sheetItems: CallSheetItemRecord[]) => {
    const unresolved = sheetItems.filter((it) => !it.lastOutcome || !it.queueState);
    if (unresolved.length === 0) return;

    const mergedUpdates: Record<string, Partial<CallSheetItemRecord>> = {};
    for (const it of unresolved) {
      let lastOutcome = it.lastOutcome;
      let lastCalledAt = it.lastCalledAt;
      if (!lastOutcome) {
        try {
          const logs = await getProspectCallLogs(currentUser.id, it.prospectId);
          const latest = logs?.[0];
          if (latest?.outcome) {
            lastOutcome = String(latest.outcome);
            const createdAtIso =
              latest?.createdAt && typeof latest.createdAt.toDate === 'function'
                ? latest.createdAt.toDate().toISOString()
                : undefined;
            lastCalledAt = (typeof latest?.timestamp === 'string' && latest.timestamp) || createdAtIso || lastCalledAt;
          }
        } catch {
          // Non-fatal: leave row as uncalled if history lookup fails.
        }
      }

      const queueState = queueStateFromOutcome(lastOutcome);
      if (!lastOutcome && queueState === 'uncalled') continue;

      const patch: Partial<CallSheetItemRecord> = {
        lastOutcome: lastOutcome || undefined,
        lastCalledAt: lastCalledAt || undefined,
        queueState
      };
      await updateCallSheetItemState(it.id, patch).catch(() => undefined);
      mergedUpdates[it.id] = patch;
    }

    if (Object.keys(mergedUpdates).length > 0) {
      setItems((prev) =>
        prev.map((it) => (mergedUpdates[it.id] ? { ...it, ...mergedUpdates[it.id] } : it))
      );
    }
  };

  const loadSheet = async (sheet: CallSheetRecord) => {
    setActiveSheet(sheet);
    try {
      localStorage.setItem(activeSheetStorageKey, sheet.id);
    } catch {
      // Ignore localStorage failures.
    }
    let its = await listCallSheetItems(currentUser.id, sheet.id);
    setItems(its);
    await backfillItemStatesFromHistory(its);
    const outbound = await listOutboundCallsForSheet(currentUser.id, sheet.id).catch(() => []);
    const latest: Record<string, OutboundCallRecord> = {};
    for (const row of outbound) {
      if (!row.prospectId) continue;
      if (!latest[row.prospectId]) latest[row.prospectId] = row;
    }
    setLatestOutboundByProspect(latest);
    // Reconcile row state from latest completed outbound call if item state is still empty.
    const mergedFromOutbound: Record<string, Partial<CallSheetItemRecord>> = {};
    for (const it of its) {
      if (it.lastOutcome) continue;
      const lastCall = latest[it.prospectId];
      if (!lastCall || lastCall.status !== 'completed') continue;
      const mappedOutcome = callSheetOutcomeFromOutbound(lastCall.outcome);
      if (!mappedOutcome) continue;
      mergedFromOutbound[it.id] = {
        lastOutcome: mappedOutcome,
        lastCalledAt: lastCall.endedAt || lastCall.startedAt || lastCall.createdAt,
        queueState: queueStateFromOutcome(mappedOutcome)
      };
      await updateCallSheetItemState(it.id, mergedFromOutbound[it.id]).catch(() => undefined);
    }
    if (Object.keys(mergedFromOutbound).length > 0) {
      its = its.map((it) => (mergedFromOutbound[it.id] ? { ...it, ...mergedFromOutbound[it.id] } : it));
      setItems(its);
    }
    await Promise.all(its.map((it) => hydrateProspect(it.prospectId)));
  };

  useEffect(() => {
    if (!activeSheet?.id) return;
    const unsubItems = subscribeCallSheetItems(
      currentUser.id,
      activeSheet.id,
      (nextItems) => {
        setItems(nextItems);
        void Promise.all(nextItems.map((it) => hydrateProspect(it.prospectId)));
      }
    );
    const unsubCalls = subscribeOutboundCallsForSheet(
      currentUser.id,
      activeSheet.id,
      (outbound) => {
        const latest: Record<string, OutboundCallRecord> = {};
        for (const row of outbound) {
          if (!row.prospectId) continue;
          if (!latest[row.prospectId]) latest[row.prospectId] = row;
        }
        setLatestOutboundByProspect(latest);
      }
    );
    return () => {
      unsubItems();
      unsubCalls();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSheet?.id, currentUser.id]);

  useEffect(() => {
    let mounted = true;
    const boot = async () => {
      setLoading(true);
      try {
        const all = await listCallSheets(currentUser.id, orgKey);
        if (!mounted) return;
        setSheets(all);
        let preferredSheet: CallSheetRecord | undefined;
        try {
          const storedId = localStorage.getItem(activeSheetStorageKey);
          preferredSheet = storedId ? all.find((s) => s.id === storedId) : undefined;
        } catch {
          preferredSheet = undefined;
        }
        const today = preferredSheet || (await getOrCreateTodayCallSheet(currentUser.id, orgKey));
        if (!mounted) return;
        if (!all.find((s) => s.id === today.id)) setSheets((prev) => [today, ...prev]);
        await loadSheet(today);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    void boot();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSheetStorageKey, currentUser.id]);

  useEffect(() => {
    const onResize = () => setIsCompact(window.innerWidth < 1200);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const bestContact = (prospectId: string): ProspectContactRecord | null => {
    const contacts = contactsMap[prospectId] || [];
    const dm = contacts.find((c) => c.isDecisionMaker && c.phone);
    if (dm) return dm;
    const anyWithPhone = contacts.find((c) => c.phone);
    return anyWithPhone || null;
  };

  const getFallbackFromSaved = (prospectId: string): ProspectSnapshot | null => {
    const byId = savedProspects.find((p) => p.id === prospectId);
    if (byId) return byId;
    const byStable = savedProspects.find((p) => toStableId(p) === prospectId);
    return byStable || null;
  };

  useEffect(() => {
    latestOutboundRef.current = latestOutboundByProspect;
  }, [latestOutboundByProspect]);

  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  useEffect(() => {
    void refreshDueCallbacks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser.id]);

  useEffect(() => {
    return () => {
      aiSequenceRunIdRef.current += 1;
      aiSequenceStopRequestedRef.current = true;
      manualSequenceRunIdRef.current += 1;
      manualSequenceStopRequestedRef.current = true;
    };
  }, []);

  useEffect(() => {
    return twilioVoiceService.subscribe((state) => {
      setManualVoiceState(state);
    });
  }, []);

  useEffect(() => {
    if (!aiSequence?.active) return;
    if (aiSequence.sheetId === activeSheet?.id) return;
    aiSequenceStopRequestedRef.current = true;
    setAiSequence((prev) =>
      prev
        ? {
            ...prev,
            stopRequested: true,
            lastError: 'AI queue paused because you switched call sheets.'
          }
        : prev
    );
  }, [activeSheet?.id, aiSequence]);

  useEffect(() => {
    if (!manualSequence?.active) return;
    if (manualSequence.sheetId === activeSheet?.id) return;
    manualSequenceStopRequestedRef.current = true;
    setManualSequence((prev) =>
      prev
        ? {
            ...prev,
            stopRequested: true,
            lastError: 'Manual queue paused because you switched call sheets.'
          }
        : prev
    );
  }, [activeSheet?.id, manualSequence]);

  const resolveGooglePlaceIdForSheetProspect = async (
    prospectId: string,
    resolveOptions?: {
      forceLookup?: boolean;
      excludePlaceIds?: string[];
      searchRadiusMi?: number;
    }
  ): Promise<string | null> => {
    const p = prospectMap[prospectId];
    const fallback = getFallbackFromSaved(prospectId);
    const excludedIds = new Set((resolveOptions?.excludePlaceIds || []).filter(Boolean));

    if (!resolveOptions?.forceLookup) {
      const directCandidates = [
        p?.placeId,
        isLikelyGooglePlaceId(prospectId) ? prospectId : undefined,
        isLikelyGooglePlaceId(fallback?.id) ? fallback?.id : undefined
      ].filter((v): v is string => typeof v === 'string' && v.length > 0 && !excludedIds.has(v));
      if (directCandidates.length > 0) return directCandidates[0];
    }

    const name = p?.name || fallback?.name;
    const address = p?.address || fallback?.address;
    const lat = p?.lat ?? fallback?.lat;
    const lng = p?.lng ?? fallback?.lng;
    if (!name || !Number.isFinite(lat) || !Number.isFinite(lng)) return null;

    const queries = Array.from(new Set([
      name,
      address ? `${name} ${address}` : ''
    ].map((value) => String(value || '').trim()).filter(Boolean)));

    for (const query of queries) {
      try {
        const lookedUp = await lookupBusinessByName(query, lat as number, lng as number, resolveOptions?.searchRadiusMi || 5);
        const recoveredPlaceId =
          (lookedUp?.placeId && isLikelyGooglePlaceId(lookedUp.placeId) ? lookedUp.placeId : null) ||
          (lookedUp?.id && isLikelyGooglePlaceId(lookedUp.id) ? lookedUp.id : null);
        if (recoveredPlaceId && !excludedIds.has(recoveredPlaceId)) return recoveredPlaceId;
      } catch {
        // Ignore lookup failures and try the next query.
      }
    }
    return null;
  };

  const fetchAndPersistProspectDetails = async (prospectId: string): Promise<{
    patch: Partial<ProspectRecord>;
    phoneImproved: boolean;
    websiteImproved: boolean;
    addressImproved: boolean;
    placeIdRecovered: boolean;
    meaningfulImprovement: boolean;
  }> => {
    const p = prospectMap[prospectId];
    const fallback = getFallbackFromSaved(prospectId);
    const currentPhone = p?.phone || fallback?.phone || '';
    const currentWebsite = p?.website || fallback?.website || '';
    const currentAddress = p?.address || fallback?.address || '';
    const currentPlaceId = p?.placeId || (isLikelyGooglePlaceId(prospectId) ? prospectId : undefined) || fallback?.id || '';

    let placeId = await resolveGooglePlaceIdForSheetProspect(prospectId);
    if (!placeId) {
      return {
        patch: {},
        phoneImproved: false,
        websiteImproved: false,
        addressImproved: false,
        placeIdRecovered: false,
        meaningfulImprovement: false
      };
    }

    try {
      let details = await getPlaceDetails(placeId);
      if (!details.phone && !details.website) {
        const alternatePlaceId = await resolveGooglePlaceIdForSheetProspect(prospectId, {
          forceLookup: true,
          excludePlaceIds: [placeId],
          searchRadiusMi: 5
        });
        if (alternatePlaceId) {
          const alternateDetails = await getPlaceDetails(alternatePlaceId);
          if (alternateDetails.phone || alternateDetails.website || alternateDetails.address) {
            placeId = alternatePlaceId;
            details = alternateDetails;
          }
        }
      }

      const patch: Partial<ProspectRecord> = {
        placeId,
        phone: details.phone || currentPhone,
        website: details.website || currentWebsite,
        address: details.address || currentAddress || '',
        city: details.city || p?.city || fallback?.city || '',
        state: details.state || p?.state || fallback?.state || '',
        zip: details.zip || p?.zip || fallback?.zip || '',
        updatedAt: nowIso()
      };
      const phoneImproved = Boolean(patch.phone && patch.phone !== currentPhone);
      const websiteImproved = Boolean(patch.website && patch.website !== currentWebsite);
      const addressImproved = Boolean(patch.address && patch.address !== currentAddress);
      const placeIdRecovered = Boolean(placeId && placeId !== currentPlaceId);
      await upsertProspect(currentUser.id, prospectId, {
        userId: currentUser.id,
        orgKey,
        ...patch
      } as any);
      setProspectMap((prev) => {
        const current = prev[prospectId];
        if (!current) return prev;
        return { ...prev, [prospectId]: { ...current, ...patch } };
      });
      return {
        patch,
        phoneImproved,
        websiteImproved,
        addressImproved,
        placeIdRecovered,
        meaningfulImprovement: phoneImproved || websiteImproved
      };
    } catch {
      return {
        patch: {},
        phoneImproved: false,
        websiteImproved: false,
        addressImproved: false,
        placeIdRecovered: false,
        meaningfulImprovement: false
      };
    }
  };

  const openCall = async (prospectId: string) => {
    const p = prospectMap[prospectId];
    const c = bestContact(prospectId);
    const fallback = getFallbackFromSaved(prospectId);
    let phone = c?.phone || p?.phone || fallback?.phone;
    if (!phone) {
      const details = await fetchAndPersistProspectDetails(prospectId);
      phone = (details.patch.phone as string) || phone;
    }
    if (phone) window.open(`tel:${phone}`, '_self');
    else alert('Phone number not available.');
  };

  const openWebsite = async (prospectId: string) => {
    const p = prospectMap[prospectId];
    const fallback = getFallbackFromSaved(prospectId);
    let url = p?.website || fallback?.website;
    if (!url) {
      const details = await fetchAndPersistProspectDetails(prospectId);
      url = (details.patch.website as string) || url;
    }
    if (url) window.open(url, '_blank');
    else alert('Website not available.');
  };

  const openDirections = (prospectId: string) => {
    const p = prospectMap[prospectId];
    const fallback = getFallbackFromSaved(prospectId);
    const addr = p?.address || fallback?.address || '';
    if (!addr) {
      alert('Address not available.');
      return;
    }
    const url = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(addr)}`;
    window.open(url, '_blank');
  };

  const hasMissingContactFields = (prospectId: string): boolean => {
    const p = prospectMap[prospectId];
    const fallback = getFallbackFromSaved(prospectId);
    const contact = bestContact(prospectId);
    const phone = contact?.phone || p?.phone || fallback?.phone;
    const website = p?.website || fallback?.website;
    return !phone || !website;
  };

  const syncProspectDetails = async (prospectId: string) => {
    setSyncingByProspect((prev) => ({ ...prev, [prospectId]: true }));
    setDetailSyncResultByProspect((prev) => {
      if (!prev[prospectId]) return prev;
      const next = { ...prev };
      delete next[prospectId];
      return next;
    });
    try {
      const result = await fetchAndPersistProspectDetails(prospectId);
      setDetailSyncResultByProspect((prev) => ({
        ...prev,
        [prospectId]: summarizeDetailSyncResult(result)
      }));
      return result;
    } finally {
      setSyncingByProspect((prev) => ({ ...prev, [prospectId]: false }));
    }
  };

  const markProspectDnc = async (prospectId: string) => {
    const p = prospectMap[prospectId];
    const name = p?.name || prospectId;
    const confirmed = window.confirm(`Mark "${name}" as Do Not Call?`);
    if (!confirmed) return;
    const now = nowIso();
    await upsertProspect(currentUser.id, prospectId, {
      userId: currentUser.id,
      dnc: true,
      dncReason: 'Marked manually from call sheet',
      dncAt: now,
      updatedAt: now
    } as any);
    setProspectMap((prev) => {
      const current = prev[prospectId];
      if (!current) return prev;
      return { ...prev, [prospectId]: { ...current, dnc: true, dncReason: 'Marked manually from call sheet', dncAt: now } };
    });
  };

  const startAiCallNow = async (
    prospectId: string,
    options?: { suppressAlert?: boolean; sheetId?: string }
  ): Promise<{ ok: boolean; detail?: string }> => {
    const sheetId = options?.sheetId || activeSheet?.id;
    if (!sheetId) return { ok: false, detail: 'No active call sheet selected.' };
    setStartingAiCallByProspect((prev) => ({ ...prev, [prospectId]: true }));
    try {
      const token = await auth.currentUser?.getIdToken();
      if (!token) {
        const detail = 'Please sign in again to start AI calls.';
        if (!options?.suppressAlert) alert(detail);
        return { ok: false, detail };
      }
      const resp = await fetch('/api/start-eleven-call-single', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          callSheetId: sheetId,
          prospectId
        })
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        const detail = toErrorText(data) || 'Failed to start AI call';
        if (!options?.suppressAlert) alert(detail);
        return { ok: false, detail };
      }
      const now = nowIso();
      setLatestOutboundByProspect((prev) => ({
        ...prev,
        [prospectId]: {
          id: data.callId,
          userId: currentUser.id,
        orgKey,
          callSheetId: sheetId,
          prospectId,
          createdAt: now,
          startedAt: now,
          status: 'dialing',
          elevenCallId: data.elevenCallId || undefined
        } as OutboundCallRecord
      }));
      return { ok: true };
    } catch (error: any) {
      const detail = toErrorText(error) || 'Failed to start AI call';
      if (!options?.suppressAlert) alert(detail);
      return { ok: false, detail };
    } finally {
      setStartingAiCallByProspect((prev) => ({ ...prev, [prospectId]: false }));
    }
  };

  const endAiCallNow = async (prospectId: string) => {
    if (!activeSheet) return;
    const active = latestOutboundByProspect[prospectId];
    if (!active || (active.status !== 'dialing' && active.status !== 'in_progress')) {
      alert('No active AI call found for this prospect.');
      return;
    }
    const confirmed = window.confirm('End this active AI call now?');
    if (!confirmed) return;
    setEndingAiCallByProspect((prev) => ({ ...prev, [prospectId]: true }));
    try {
      const token = await auth.currentUser?.getIdToken();
      if (!token) {
        alert('Please sign in again to end AI calls.');
        return;
      }
      const resp = await fetch('/api/end-eleven-call-single', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          callSheetId: activeSheet.id,
          prospectId,
          callId: active.id
        })
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        const detail = toErrorText(data) || 'Failed to end AI call';
        alert(detail);
        return;
      }
      const now = nowIso();
      setLatestOutboundByProspect((prev) => ({
        ...prev,
        [prospectId]: {
          ...(prev[prospectId] || active),
          status: 'completed',
          outcome: 'failed',
          endedAt: now
        } as OutboundCallRecord
      }));
      setItems((prev) =>
        prev.map((it) =>
          it.prospectId === prospectId
            ? { ...it, lastOutcome: it.lastOutcome || 'Called', lastCalledAt: now, queueState: 'called' }
            : it
        )
      );
    } finally {
      setEndingAiCallByProspect((prev) => ({ ...prev, [prospectId]: false }));
    }
  };

  const startManualCallNow = async (
    prospectId: string,
    options?: { suppressAlert?: boolean; sheetId?: string }
  ): Promise<{ ok: boolean; detail?: string }> => {
    const sheetId = options?.sheetId || activeSheet?.id;
    if (!sheetId) return { ok: false, detail: 'No active call sheet selected.' };
    setStartingManualCallByProspect((prev) => ({ ...prev, [prospectId]: true }));
    try {
      const token = await auth.currentUser?.getIdToken();
      if (!token) {
        const detail = 'Please sign in again to start manual browser calls.';
        if (!options?.suppressAlert) alert(detail);
        return { ok: false, detail };
      }

      const currentManual = twilioVoiceService.getState();
      if (currentManual.prospectId && currentManual.prospectId !== prospectId && currentManual.status !== 'idle' && currentManual.status !== 'ready') {
        const detail = 'Another manual browser call is already active.';
        if (!options?.suppressAlert) alert(detail);
        return { ok: false, detail };
      }

      const resp = await fetch('/api/start-manual-call', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          callSheetId: sheetId,
          prospectId
        })
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        const detail = toErrorText(data) || 'Failed to start manual browser call';
        if (!options?.suppressAlert) alert(detail);
        return { ok: false, detail };
      }

      await twilioVoiceService.startCall({
        authToken: token,
        prospectId,
        callId: data.callId,
        callSheetId: sheetId,
        toNumber: data.toNumber
      });

      const now = nowIso();
      setLatestOutboundByProspect((prev) => ({
        ...prev,
        [prospectId]: {
          id: data.callId,
          userId: currentUser.id,
        orgKey,
          callSheetId: sheetId,
          prospectId,
          createdAt: now,
          startedAt: now,
          status: 'dialing',
          toNumber: data.toNumber || undefined,
          callMode: 'manual',
          transport: 'twilio_browser'
        } as OutboundCallRecord
      }));
      return { ok: true };
    } catch (error: any) {
      const detail = toErrorText(error) || 'Failed to start manual browser call';
      if (!options?.suppressAlert) alert(detail);
      return { ok: false, detail };
    } finally {
      setStartingManualCallByProspect((prev) => ({ ...prev, [prospectId]: false }));
    }
  };

  const endManualCallNow = async (prospectId: string) => {
    const currentManual = twilioVoiceService.getState();
    if (currentManual.prospectId !== prospectId || (currentManual.status !== 'connecting' && currentManual.status !== 'connected')) {
      alert('No active manual browser call found for this prospect.');
      return;
    }
    setEndingManualCallByProspect((prev) => ({ ...prev, [prospectId]: true }));
    try {
      await twilioVoiceService.endCall();
    } finally {
      setEndingManualCallByProspect((prev) => ({ ...prev, [prospectId]: false }));
    }
  };

  const refreshAiCallStatus = async (
    prospectId: string,
    callId?: string
  ): Promise<{ active: boolean; failed: boolean }> => {
    if (!activeSheet) return { active: true, failed: false };
    try {
      const token = await auth.currentUser?.getIdToken();
      if (!token) return { active: true, failed: false };
      const resp = await fetch('/api/refresh-outbound-call-status', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          callSheetId: activeSheet.id,
          prospectId,
          callId
        })
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) return { active: true, failed: false };
      const refreshed = data?.call as Partial<OutboundCallRecord> | undefined;
      if (refreshed?.id) {
        setLatestOutboundByProspect((prev) => ({
          ...prev,
          [prospectId]: {
            ...(prev[prospectId] || {}),
            ...(refreshed as OutboundCallRecord)
          }
        }));
      }
      const refreshedStatus = refreshed?.status;
      const active =
        typeof data?.active === 'boolean'
          ? data.active
          : isActiveOutboundStatus(refreshedStatus as OutboundCallRecord['status']);
      const failed =
        refreshed?.status === 'failed' ||
        refreshed?.outcome === 'failed' ||
        String(refreshed?.providerOutcome || '').toLowerCase() === 'failed';
      return { active, failed };
    } catch {
      return { active: true, failed: false };
    }
  };

  const waitForAiCallSettlement = async (
    prospectId: string,
    runId: number
  ): Promise<{ timedOut: boolean; failed: boolean }> => {
    const startedAt = Date.now();
    const maxWaitMs = 45 * 60 * 1000;
    let lastProbeAt = 0;
    while (Date.now() - startedAt < maxWaitMs) {
      if (aiSequenceRunIdRef.current !== runId) {
        return { timedOut: false, failed: false };
      }
      const latest = latestOutboundRef.current[prospectId];
      if (latest && !isActiveOutboundStatus(latest.status)) {
        return {
          timedOut: false,
          failed: latest.status === 'failed' || latest.outcome === 'failed'
        };
      }
      const currentItem = itemsRef.current.find((it) => it.prospectId === prospectId);
      const latestCreatedMs = latest ? new Date(latest.createdAt).getTime() : 0;
      const itemCalledMs = currentItem?.lastCalledAt ? new Date(currentItem.lastCalledAt).getTime() : 0;
      const itemQueueState = currentItem?.queueState;
      if (
        currentItem &&
        itemCalledMs > 0 &&
        latestCreatedMs > 0 &&
        itemCalledMs >= latestCreatedMs &&
        itemQueueState &&
        itemQueueState !== 'uncalled'
      ) {
        return {
          timedOut: false,
          failed: itemQueueState === 'rejected'
        };
      }
      if (latest && Date.now() - lastProbeAt >= 10000) {
        lastProbeAt = Date.now();
        const refreshed = await refreshAiCallStatus(prospectId, latest.id);
        if (!refreshed.active) {
          return { timedOut: false, failed: refreshed.failed };
        }
      }
      await sleep(1500);
    }
    return { timedOut: true, failed: true };
  };

  const waitForManualCallSettlement = async (
    prospectId: string,
    runId: number
  ): Promise<{ timedOut: boolean; failed: boolean }> => {
    const startedAt = Date.now();
    const maxWaitMs = 45 * 60 * 1000;
    while (Date.now() - startedAt < maxWaitMs) {
      if (manualSequenceRunIdRef.current !== runId) {
        return { timedOut: false, failed: false };
      }
      const latest = latestOutboundRef.current[prospectId];
      const currentManual = twilioVoiceService.getState();
      const activeManual =
        currentManual.prospectId === prospectId &&
        (currentManual.status === 'connecting' || currentManual.status === 'connected' || currentManual.status === 'disconnecting');
      if (!activeManual && latest && !isActiveOutboundStatus(latest.status)) {
        return {
          timedOut: false,
          failed: latest.status === 'failed' || latest.outcome === 'failed'
        };
      }
      if (!activeManual && !latest) {
        return { timedOut: false, failed: false };
      }
      await sleep(1000);
    }
    return { timedOut: true, failed: true };
  };

  const logOutcome = async (prospectId: string, response: string) => {
    const p = prospectMap[prospectId];
    const fallback = getFallbackFromSaved(prospectId);
    const displayName = p?.name || fallback?.name || prospectId;
    const note = (notesByProspect[prospectId] || '').trim();
    const entry = {
      name: displayName,
      category: p?.categoryLabel || fallback?.category || 'Business',
      phone: p?.phone || fallback?.phone || '',
      website: p?.website || fallback?.website || '',
      address: p?.address || fallback?.address || '',
      timestamp: new Date().toISOString(),
      outcome: response,
      note
    };
    await saveCallLog(currentUser.id, prospectId, { ...entry, orgKey }, orgKey);
    const now = nowIso();
    const queueState = queueStateFromOutcome(response);
    const defaultCallbackAt = (() => {
      const d = new Date();
      d.setDate(d.getDate() + 1);
      d.setHours(9, 0, 0, 0);
      return d.toISOString();
    })();
    const nextReminderAt =
      response === 'Call Back'
        ? ((p?.nextReminderAt && new Date(p.nextReminderAt).getTime() > Date.now()) ? p.nextReminderAt : defaultCallbackAt)
        : p?.nextReminderAt;
    await upsertProspect(currentUser.id, prospectId, {
      userId: currentUser.id,
      lastOutcome: response,
      lastOutcomeAt: now,
      callbackPending: response === 'Call Back',
      nextReminderAt,
      lastActivityAt: now,
      updatedAt: now
    } as any).catch(() => undefined);
    const item = items.find((it) => it.prospectId === prospectId);
    if (item) {
      await updateCallSheetItemState(item.id, {
        lastOutcome: response,
        lastCalledAt: now,
        queueState
      }).catch(() => undefined);
      setItems((prev) =>
        prev.map((it) =>
          it.id === item.id ? { ...it, lastOutcome: response, lastCalledAt: now, queueState } : it
        )
      );
    }
    setProspectMap((prev) => {
      const current = prev[prospectId];
      if (!current) return prev;
      return {
        ...prev,
        [prospectId]: {
          ...current,
          lastOutcome: response as any,
          lastOutcomeAt: now,
          callbackPending: response === 'Call Back',
          nextReminderAt,
          lastActivityAt: now,
          updatedAt: now
        }
      };
    });
    if (response === 'Rejected') {
      // Rejected prospects should no longer remain in Saved.
      const idCandidates = Array.from(
        new Set(
          [prospectId, p?.placeId, p?.businessHash, fallback?.id].filter(
            (v): v is string => typeof v === 'string' && v.length > 0
          )
        )
      );
      await Promise.all(
        idCandidates.map((id) =>
          removeSavedProspectByUserAndBusiness(currentUser.id, id).catch(() => undefined)
        )
      );
      await setProspectSaved(currentUser.id, prospectId, false).catch(() => undefined);
      if (onRejectedRemoveSaved) {
        await onRejectedRemoveSaved(idCandidates);
      }
    }
    setNotesByProspect((prev) => ({ ...prev, [prospectId]: '' }));
    await refreshDueCallbacks().catch(() => undefined);
  };

  const openAppointmentCapture = (prospectId: string, response: string) => {
    const now = new Date();
    const plus30 = roundToQuarter(new Date(now.getTime() + 30 * 60 * 1000));
    const p = prospectMap[prospectId];
    const fallback = getFallbackFromSaved(prospectId);
    const contact = bestContact(prospectId);
    const date = plus30.toISOString().slice(0, 10);
    const roundedHour = clamp(plus30.getHours(), 7, 21);
    const roundedMinute = [0, 15, 30, 45].includes(plus30.getMinutes()) ? plus30.getMinutes() : 0;
    const time = toTimeString(roundedHour, roundedMinute);
    setAppointmentDraft({
      prospectId,
      outcome: response,
      date,
      time,
      durationMinutes: 30,
      appointmentType: 'in_person',
      appointmentLocation: p?.address || fallback?.address || '',
      appointmentContactName: contact?.name || ''
    });
  };

  const saveAppointmentOutcome = async () => {
    if (!appointmentDraft) return;
    if (!appointmentDraft.date || !appointmentDraft.time) {
      alert('Appointment date and time are required.');
      return;
    }
    const { hour, minute } = splitTime(appointmentDraft.time);
    if (!(hour >= 7 && hour <= 21)) {
      alert('Appointment hour must be between 7:00 AM and 9:00 PM.');
      return;
    }
    if (![0, 15, 30, 45].includes(minute)) {
      alert('Appointment minutes must be :00, :15, :30, or :45.');
      return;
    }
    const appointmentAt = new Date(`${appointmentDraft.date}T${appointmentDraft.time}:00`);
    if (Number.isNaN(appointmentAt.getTime())) {
      alert('Invalid appointment date/time.');
      return;
    }
    setSavingAppointment(true);
    try {
      const p = prospectMap[appointmentDraft.prospectId];
      const fallback = getFallbackFromSaved(appointmentDraft.prospectId);
      const displayName = p?.name || fallback?.name || appointmentDraft.prospectId;
      const note = (notesByProspect[appointmentDraft.prospectId] || '').trim();
      const entry = {
        name: displayName,
        category: p?.categoryLabel || fallback?.category || 'Business',
        phone: p?.phone || fallback?.phone || '',
        website: p?.website || fallback?.website || '',
        address: p?.address || fallback?.address || '',
        timestamp: new Date().toISOString(),
        outcome: appointmentDraft.outcome,
        note,
        appointmentAt: appointmentAt.toISOString(),
        durationMinutes: Number(appointmentDraft.durationMinutes) || 30,
        appointmentType: appointmentDraft.appointmentType,
        appointmentLocation: appointmentDraft.appointmentLocation.trim(),
        appointmentContactName: appointmentDraft.appointmentContactName.trim()
      };
      await saveCallLog(currentUser.id, appointmentDraft.prospectId, { ...entry, orgKey }, orgKey);
      const now = nowIso();
      const queueState = queueStateFromOutcome(appointmentDraft.outcome);
      await upsertProspect(currentUser.id, appointmentDraft.prospectId, {
        userId: currentUser.id,
        orgKey,
        lastOutcome: appointmentDraft.outcome,
        lastOutcomeAt: now,
        callbackPending: false,
        nextReminderAt: null,
        nextAppointmentAt: appointmentAt.toISOString(),
        lastActivityAt: now,
        updatedAt: now
      } as any).catch(() => undefined);
      const item = items.find((it) => it.prospectId === appointmentDraft.prospectId);
      if (item) {
        await updateCallSheetItemState(item.id, {
          lastOutcome: appointmentDraft.outcome,
          lastCalledAt: now,
          queueState
        }).catch(() => undefined);
        setItems((prev) =>
          prev.map((it) =>
            it.id === item.id
              ? { ...it, lastOutcome: appointmentDraft.outcome, lastCalledAt: now, queueState }
              : it
          )
        );
      }
      setProspectMap((prev) => {
        const current = prev[appointmentDraft.prospectId];
        if (!current) return prev;
        return {
          ...prev,
          [appointmentDraft.prospectId]: {
            ...current,
            lastOutcome: appointmentDraft.outcome as any,
            lastOutcomeAt: now,
            callbackPending: false,
            nextReminderAt: undefined,
            nextAppointmentAt: appointmentAt.toISOString(),
            lastActivityAt: now,
            updatedAt: now
          }
        };
      });
      setNotesByProspect((prev) => ({ ...prev, [appointmentDraft.prospectId]: '' }));
      setAppointmentDraft(null);
      await refreshDueCallbacks().catch(() => undefined);
    } finally {
      setSavingAppointment(false);
    }
  };

  const handleOutcomeClick = async (prospectId: string, response: string) => {
    if (APPOINTMENT_OUTCOMES.has(response)) {
      openAppointmentCapture(prospectId, response);
      return;
    }
    await logOutcome(prospectId, response);
  };

  const setProspectQuality = async (prospectId: string, score: 1 | 2 | 3 | 4 | 5) => {
    await upsertProspect(currentUser.id, prospectId, {
      userId: currentUser.id,
      targetQualityScore: score,
      updatedAt: nowIso()
    } as any);
    setProspectMap((prev) => {
      const current = prev[prospectId];
      if (!current) return prev;
      return { ...prev, [prospectId]: { ...current, targetQualityScore: score, updatedAt: nowIso() } };
    });
  };

  const hourOptions = useMemo(() => Array.from({ length: 15 }, (_, i) => i + 7), []);
  const minuteOptions = useMemo(() => [0, 15, 30, 45], []);

  const toStableId = (snapshot: ProspectSnapshot): string => {
    return isLikelyGooglePlaceId(snapshot.id)
      ? snapshot.id
      : aiService.generateBusinessHash({
          name: snapshot.name,
          category: snapshot.category || 'Business',
          address: snapshot.address || '',
          distance: 0,
          types: [snapshot.category || 'Business'],
          businessStatus: 'Unknown',
          website: snapshot.website || '',
          phone: snapshot.phone || ''
        });
  };

  const addFromSaved = async (snapshot: ProspectSnapshot) => {
    if (!activeSheet) return;
    const stableId = toStableId(snapshot);
    setAddingByStableId((prev) => ({ ...prev, [stableId]: true }));
    try {
      await upsertProspect(currentUser.id, stableId, {
        id: stableId,
        userId: currentUser.id,
        orgKey,
        placeId: isLikelyGooglePlaceId(snapshot.id) ? snapshot.id : undefined,
        businessHash: !isLikelyGooglePlaceId(snapshot.id) ? stableId : undefined,
        name: snapshot.name,
        address: snapshot.address || '',
        city: snapshot.city || '',
        state: snapshot.state || '',
        zip: snapshot.zip || '',
        lat: snapshot.lat,
        lng: snapshot.lng,
        categoryLabel: snapshot.category,
        phone: snapshot.phone,
        website: snapshot.website,
        saved: true,
        status: 'saved',
        createdAt: nowIso(),
        updatedAt: nowIso()
      } as any);

      await addProspectToCallSheet(currentUser.id, activeSheet.id, stableId, orgKey);
      const updated = await listCallSheetItems(currentUser.id, activeSheet.id);
      setItems(updated);
      await hydrateProspect(stableId, true);
    } finally {
      setAddingByStableId((prev) => ({ ...prev, [stableId]: false }));
    }
  };

  const queuedCount = items.length;
  const calledCount = items.filter((it) => Boolean(it.lastOutcome)).length;
  const callbackCount = items.filter((it) => queueStateFromOutcome(it.lastOutcome) === 'callback').length;
  const appointmentCount = items.filter((it) => queueStateFromOutcome(it.lastOutcome) === 'appointment').length;
  const uncalledCount = items.filter((it) => queueStateFromOutcome(it.lastOutcome) === 'uncalled').length;
  const completedCount = calledCount;
  const aiQueueEligibleItems = items.filter((it) => {
    const queueState = queueStateFromOutcome(it.lastOutcome);
    return queueState === 'uncalled' || queueState === 'callback';
  });
  const aiQueueTargetItems = aiQueueEligibleItems.filter((it) => {
    const p = prospectMap[it.prospectId];
    const fallback = getFallbackFromSaved(it.prospectId);
    const phone = bestContact(it.prospectId)?.phone || p?.phone || fallback?.phone;
    const latest = latestOutboundByProspect[it.prospectId];
    return !!phone && !p?.dnc && !isActiveOutboundStatus(latest?.status);
  });
  const aiQueueSkippedCount = Math.max(0, aiQueueEligibleItems.length - aiQueueTargetItems.length);
  const manualQueueTargetItems = aiQueueTargetItems;
  const manualQueueSkippedCount = aiQueueSkippedCount;
  const aiSequenceCurrentLabel = aiSequence?.currentProspectId
    ? prospectMap[aiSequence.currentProspectId]?.name ||
      getFallbackFromSaved(aiSequence.currentProspectId)?.name ||
      aiSequence.currentProspectId
    : '';
  const manualSequenceCurrentLabel = manualSequence?.currentProspectId
    ? prospectMap[manualSequence.currentProspectId]?.name ||
      getFallbackFromSaved(manualSequence.currentProspectId)?.name ||
      manualSequence.currentProspectId
    : '';
  const dueCallbacksNotInSheet = dueCallbacks.filter((p) => !items.some((it) => it.prospectId === p.id));

  const prospectIdsInSheet = useMemo(() => new Set(items.map((it) => it.prospectId)), [items]);
  const quickAddCandidates = useMemo(() => {
    const q = quickAddQuery.trim().toLowerCase();
    return savedProspects
      .filter((p) => !prospectIdsInSheet.has(p.id) && !prospectIdsInSheet.has(toStableId(p)))
      .filter((p) => {
        if (!q) return true;
        return (
          p.name.toLowerCase().includes(q) ||
          (p.category || '').toLowerCase().includes(q) ||
          (p.address || '').toLowerCase().includes(q)
        );
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [savedProspects, quickAddQuery, prospectIdsInSheet]);
  const quickAddVisible = showAllQuickAdd ? quickAddCandidates : quickAddCandidates.slice(0, 12);
  const filteredItems = items.filter((it) => rowFilter === 'all' || queueStateFromOutcome(it.lastOutcome) === rowFilter);
  const nextUncalled = items.find((it) => queueStateFromOutcome(it.lastOutcome) === 'uncalled');
  const missingContactCount = items.reduce(
    (count, it) => (hasMissingContactFields(it.prospectId) ? count + 1 : count),
    0
  );
  const filteredSheets = useMemo(() => {
    const q = sheetSearchQuery.trim().toLowerCase();
    if (!q) return sheets;
    return sheets.filter((sheet) => {
      const label = formatSheetDisplayName(sheet, todayKey, currentWeekStartKey).toLowerCase();
      return (
        label.includes(q) ||
        sheet.name.toLowerCase().includes(q) ||
        sheet.dateKey.includes(q)
      );
    });
  }, [currentWeekStartKey, sheetSearchQuery, sheets, todayKey]);
  const visibleSheets = showAllSheets || sheetSearchQuery.trim() ? filteredSheets : filteredSheets.slice(0, 8);
  const hiddenSheetCount = Math.max(0, filteredSheets.length - visibleSheets.length);

  const sheetTitle = activeSheet ? formatSheetDisplayName(activeSheet, todayKey, currentWeekStartKey) : 'Call Sheet';

  const patchAiSequence = (patch: Partial<QueueSequenceState>) => {
    setAiSequence((prev) => (prev ? { ...prev, ...patch } : prev));
  };

  const patchManualSequence = (patch: Partial<QueueSequenceState>) => {
    setManualSequence((prev) => (prev ? { ...prev, ...patch } : prev));
  };

  const startAiCallSheetQueue = async () => {
    if (!activeSheet) return;
    const targets = aiQueueTargetItems;
    if (targets.length === 0) {
      alert('No callable uncalled or callback prospects are available on this sheet.');
      return;
    }
    const confirmed = window.confirm(
      `Start AI queue for ${targets.length} prospects on "${sheetTitle}"? This will work down the sheet one call at a time.`
    );
    if (!confirmed) return;

    const runId = aiSequenceRunIdRef.current + 1;
    aiSequenceRunIdRef.current = runId;
    aiSequenceStopRequestedRef.current = false;
    setAiSequence({
      active: true,
      stopRequested: false,
      sheetId: activeSheet.id,
      startedAt: nowIso(),
      total: targets.length,
      completed: 0,
      failed: 0,
      skipped: aiQueueSkippedCount,
      currentProspectId: null,
      lastError: null
    });

    let completed = 0;
    let failed = 0;

    for (const item of targets) {
      if (aiSequenceRunIdRef.current !== runId || aiSequenceStopRequestedRef.current) break;
      patchAiSequence({ currentProspectId: item.prospectId });

      const started = await startAiCallNow(item.prospectId, {
        suppressAlert: true,
        sheetId: activeSheet.id
      });

      if (!started.ok) {
        failed += 1;
        patchAiSequence({
          failed,
          currentProspectId: null,
          lastError: started.detail || 'Failed to start AI call.'
        });
        continue;
      }

      const settled = await waitForAiCallSettlement(item.prospectId, runId);
      if (aiSequenceRunIdRef.current !== runId) break;
      if (settled.timedOut) {
        failed += 1;
        patchAiSequence({
          failed,
          currentProspectId: null,
          lastError: 'Timed out waiting for the current AI call to finish.'
        });
        break;
      }

      if (settled.failed) failed += 1;
      else completed += 1;

      patchAiSequence({
        completed,
        failed,
        currentProspectId: null
      });

      if (aiSequenceStopRequestedRef.current) break;
      await sleep(1250);
    }

    if (aiSequenceRunIdRef.current === runId) {
      const stopRequested = aiSequenceStopRequestedRef.current;
      setAiSequence((prev) =>
        prev
          ? {
              ...prev,
              active: false,
              stopRequested,
              currentProspectId: null,
              completed,
              failed
            }
          : prev
      );
    }
    aiSequenceStopRequestedRef.current = false;
  };

  const stopAiCallSheetQueue = () => {
    if (!aiSequence?.active) return;
    aiSequenceStopRequestedRef.current = true;
    setAiSequence((prev) =>
      prev
        ? {
            ...prev,
            stopRequested: true
          }
        : prev
    );
  };

  const startManualCallSheetQueue = async () => {
    if (!activeSheet) return;
    const targets = manualQueueTargetItems;
    if (targets.length === 0) {
      alert('No callable uncalled or callback prospects are available on this sheet.');
      return;
    }
    const confirmed = window.confirm(
      `Start manual browser calling for ${targets.length} prospects on "${sheetTitle}"? This will dial down the sheet one call at a time.`
    );
    if (!confirmed) return;

    const runId = manualSequenceRunIdRef.current + 1;
    manualSequenceRunIdRef.current = runId;
    manualSequenceStopRequestedRef.current = false;
    setManualSequence({
      active: true,
      stopRequested: false,
      sheetId: activeSheet.id,
      startedAt: nowIso(),
      total: targets.length,
      completed: 0,
      failed: 0,
      skipped: manualQueueSkippedCount,
      currentProspectId: null,
      lastError: null
    });

    let completed = 0;
    let failed = 0;

    for (const item of targets) {
      if (manualSequenceRunIdRef.current !== runId || manualSequenceStopRequestedRef.current) break;
      patchManualSequence({ currentProspectId: item.prospectId });

      const started = await startManualCallNow(item.prospectId, {
        suppressAlert: true,
        sheetId: activeSheet.id
      });
      if (!started.ok) {
        failed += 1;
        patchManualSequence({
          failed,
          currentProspectId: null,
          lastError: started.detail || 'Failed to start manual browser call.'
        });
        continue;
      }

      const settled = await waitForManualCallSettlement(item.prospectId, runId);
      if (manualSequenceRunIdRef.current !== runId) break;
      if (settled.timedOut) {
        failed += 1;
        patchManualSequence({
          failed,
          currentProspectId: null,
          lastError: 'Timed out waiting for the current manual call to finish.'
        });
        break;
      }

      if (settled.failed) failed += 1;
      else completed += 1;

      patchManualSequence({
        completed,
        failed,
        currentProspectId: null
      });

      if (manualSequenceStopRequestedRef.current) break;
      await sleep(1250);
    }

    if (manualSequenceRunIdRef.current === runId) {
      const stopRequested = manualSequenceStopRequestedRef.current;
      setManualSequence((prev) =>
        prev
          ? {
              ...prev,
              active: false,
              stopRequested,
              currentProspectId: null,
              completed,
              failed
            }
          : prev
      );
    }
    manualSequenceStopRequestedRef.current = false;
  };

  const stopManualCallSheetQueue = () => {
    if (!manualSequence?.active) return;
    manualSequenceStopRequestedRef.current = true;
    setManualSequence((prev) =>
      prev
        ? {
            ...prev,
            stopRequested: true
          }
        : prev
    );
  };

  const addDueCallbackToCurrentSheet = async (prospectId: string) => {
    if (!activeSheet) return;
    if (items.some((it) => it.prospectId === prospectId)) return;
    setAddingDueCallbackIds((prev) => ({ ...prev, [prospectId]: true }));
    try {
      await addProspectToCallSheet(currentUser.id, activeSheet.id, prospectId, orgKey);
      const updated = await listCallSheetItems(currentUser.id, activeSheet.id);
      setItems(updated);
      await hydrateProspect(prospectId, true);
    } finally {
      setAddingDueCallbackIds((prev) => ({ ...prev, [prospectId]: false }));
    }
  };

  const addAllDueCallbacksToCurrentSheet = async () => {
    if (!activeSheet) return;
    const targets = dueCallbacksNotInSheet;
    if (targets.length === 0) {
      alert('All due callbacks are already on this sheet.');
      return;
    }
    setAddingAllDueCallbacks(true);
    try {
      for (const prospect of targets) {
        await addProspectToCallSheet(currentUser.id, activeSheet.id, prospect.id, orgKey);
      }
      const updated = await listCallSheetItems(currentUser.id, activeSheet.id);
      setItems(updated);
      await Promise.all(targets.map((prospect) => hydrateProspect(prospect.id, true)));
      alert(`Added ${targets.length} due callbacks to this week.`);
    } finally {
      setAddingAllDueCallbacks(false);
    }
  };

  const getFutureWeekOptions = (baseDateKey?: string, count = 6): Date[] => {
    const base = startOfWeekMonday(baseDateKey ? parseDateKeyAsLocal(baseDateKey) : new Date());
    return Array.from({ length: count }, (_, idx) => {
      const next = new Date(base);
      next.setDate(base.getDate() + (idx + 1) * 7);
      return startOfWeekMonday(next);
    });
  };

  const moveRowToWeek = async (item: CallSheetItemRecord, targetWeek: Date) => {
    if (!activeSheet) return;
    setMovingByItemId((prev) => ({ ...prev, [item.id]: true }));
    try {
      const targetSheet = await getOrCreateCallSheetForWeek(currentUser.id, targetWeek, orgKey);
      await addProspectToCallSheet(currentUser.id, targetSheet.id, item.prospectId, orgKey);
      await removeCallSheetItem(item.id);
      const refreshed = await listCallSheets(currentUser.id, orgKey);
      setSheets(refreshed);
      if (activeSheet.id === item.sheetId) {
        setItems((prev) => prev.filter((it) => it.id !== item.id));
      }
      alert(`Moved to ${formatWeekOfLabel(targetWeek)}.`);
    } finally {
      setMovingByItemId((prev) => ({ ...prev, [item.id]: false }));
    }
  };

  const promptMoveRowToWeek = async (item: CallSheetItemRecord) => {
    const options = getFutureWeekOptions(activeSheet?.dateKey);
    const choice = window.prompt(
      `Move this prospect to which week?\n${options
        .map((week, idx) => `${idx + 1}) ${formatWeekOfLabel(week)}`)
        .join('\n')}\n\nEnter a number (blank = next week).`,
      ''
    );
    const idx = Number(choice);
    const selected = Number.isFinite(idx) && idx > 0 && idx <= options.length ? options[idx - 1] : options[0];
    await moveRowToWeek(item, selected);
  };

  const removeRowFromSheet = async (item: CallSheetItemRecord) => {
    const label = prospectMap[item.prospectId]?.name || getFallbackFromSaved(item.prospectId)?.name || item.prospectId;
    const confirmed = window.confirm(`Remove "${label}" from this call sheet?`);
    if (!confirmed) return;
    setRemovingByItemId((prev) => ({ ...prev, [item.id]: true }));
    try {
      await removeCallSheetItem(item.id);
      setItems((prev) => prev.filter((it) => it.id !== item.id));
    } finally {
      setRemovingByItemId((prev) => {
        const next = { ...prev };
        delete next[item.id];
        return next;
      });
    }
  };

  const carryUncalledToNextWeek = async () => {
    if (!activeSheet) return;
    const uncalledItems = items.filter((it) => queueStateFromOutcome(it.lastOutcome) === 'uncalled');
    if (uncalledItems.length === 0) {
      alert('No uncalled prospects to carry forward.');
      return;
    }
    const nextWeek = getFutureWeekOptions(activeSheet.dateKey, 1)[0];
    const confirmed = window.confirm(
      `Move ${uncalledItems.length} uncalled prospects to ${formatWeekOfLabel(nextWeek)}?`
    );
    if (!confirmed) return;
    setCarryingForward(true);
    try {
      const targetSheet = await getOrCreateCallSheetForWeek(currentUser.id, nextWeek, orgKey);
      for (const item of uncalledItems) {
        await addProspectToCallSheet(currentUser.id, targetSheet.id, item.prospectId, orgKey);
        await removeCallSheetItem(item.id);
      }
      const refreshed = await listCallSheets(currentUser.id, orgKey);
      setSheets(refreshed);
      await loadSheet(targetSheet);
      alert(`Moved ${uncalledItems.length} prospects to ${formatWeekOfLabel(nextWeek)}.`);
    } finally {
      setCarryingForward(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.topBar}>
        <div>
          <h3 style={{ margin: 0 }}>{sheetTitle}</h3>
          <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: 12 }}>
            Work your queue quickly, then open CRM details for contacts and reminders.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            style={{ ...styles.select, minWidth: 220, textAlign: 'left', background: '#fff' }}
            onClick={() => setShowSheetPicker(true)}
          >
            {activeSheet ? formatSheetDisplayName(activeSheet, todayKey, currentWeekStartKey) : 'Choose call sheet'}
          </button>
          <button
            style={btnStyle}
            onClick={async () => {
              const name = window.prompt('Name this call sheet (example: Attorneys - Northside):', '');
              if (!name || !name.trim()) return;
              const dk = window.prompt('Date (YYYY-MM-DD). Leave blank for today:', todayKey) || todayKey;
              setLoading(true);
              try {
                const dateKey = dk.trim() || todayKey;
                const id = await createCallSheet(currentUser.id, name.trim(), dateKey, orgKey);
                const created: CallSheetRecord = {
                  id,
                  userId: currentUser.id,
        orgKey,
                  name: name.trim(),
                  dateKey,
                  createdAt: nowIso(),
                  updatedAt: nowIso()
                };
                setSheets((prev) => [created, ...prev.filter((s) => s.id !== id)]);
                await loadSheet(created);
                const all = await listCallSheets(currentUser.id, orgKey);
                setSheets(all);
              } finally {
                setLoading(false);
              }
            }}
          >
            New sheet
          </button>
          <button
            style={{ ...btnStyle, borderColor: '#93c5fd' }}
            onClick={async () => {
              if (!activeSheet) return;
              const name = (window.prompt('Business name for manual call entry:', '') || '').trim();
              if (!name) return;
              const phone = (window.prompt('Phone number to dial (your number for test):', '') || '').trim();
              if (!phone) {
                alert('Phone number is required.');
                return;
              }
              const website = (window.prompt('Website (optional):', '') || '').trim();
              const address = (window.prompt('Address (optional):', '') || '').trim();
              const now = nowIso();
              const manualProspectId = `prospect_manual_${Date.now()}`;
              await upsertProspect(currentUser.id, manualProspectId, {
                id: manualProspectId,
                userId: currentUser.id,
        orgKey,
                name,
                phone,
                website: website || undefined,
                address: address || '',
                city: '',
                state: '',
                zip: '',
                categoryLabel: 'Manual Test',
                saved: false,
                status: 'working',
                createdAt: now,
                updatedAt: now
              } as any);
              await addProspectToCallSheet(currentUser.id, activeSheet.id, manualProspectId, orgKey);
              const updated = await listCallSheetItems(currentUser.id, activeSheet.id);
              setItems(updated);
              await hydrateProspect(manualProspectId, true);
            }}
          >
            Manual Add Call
          </button>
          <button
            style={{ ...btnStyle, borderColor: '#93c5fd', background: aiSequence?.active ? '#eff6ff' : '#fff' }}
            onClick={() => void startAiCallSheetQueue()}
            disabled={!activeSheet || !!aiSequence?.active || aiQueueTargetItems.length === 0}
            title={
              aiQueueTargetItems.length === 0
                ? 'No callable uncalled or callback prospects are ready on this sheet.'
                : 'Start AI working down the call sheet one row at a time.'
            }
          >
            {aiSequence?.active ? 'AI Queue Running...' : `AI Call Sheet (${aiQueueTargetItems.length})`}
          </button>
          {aiSequence?.active ? (
            <button
              style={{ ...btnStyle, borderColor: '#fecaca', color: '#b91c1c' }}
              onClick={() => stopAiCallSheetQueue()}
              disabled={!!aiSequence?.stopRequested}
            >
              {aiSequence?.stopRequested ? 'Stopping queue...' : 'Stop AI Queue'}
            </button>
          ) : null}
          <button
            style={{ ...btnStyle, borderColor: '#93c5fd', background: manualSequence?.active ? '#eff6ff' : '#fff' }}
            onClick={() => void startManualCallSheetQueue()}
            disabled={!activeSheet || !!manualSequence?.active || manualQueueTargetItems.length === 0}
            title={
              manualQueueTargetItems.length === 0
                ? 'No callable uncalled or callback prospects are ready on this sheet.'
                : 'Start browser-based manual dialing down the call sheet.'
            }
          >
            {manualSequence?.active ? 'Manual Queue Running...' : `Manual Call Sheet (${manualQueueTargetItems.length})`}
          </button>
          {manualSequence?.active ? (
            <button
              style={{ ...btnStyle, borderColor: '#fecaca', color: '#b91c1c' }}
              onClick={() => stopManualCallSheetQueue()}
              disabled={!!manualSequence?.stopRequested}
            >
              {manualSequence?.stopRequested ? 'Stopping manual queue...' : 'Stop Manual Queue'}
            </button>
          ) : null}
          <button
            style={{ ...btnStyle, borderColor: showQuickAddPanel ? '#93c5fd' : '#e5e7eb' }}
            onClick={() => setShowQuickAddPanel((v) => !v)}
          >
            {showQuickAddPanel ? 'Hide quick add' : `Quick add (${quickAddCandidates.length})`}
          </button>
          <button
            style={{ ...btnStyle, borderColor: '#bfdbfe', opacity: syncingMissingDetails ? 0.7 : 1 }}
            onClick={async () => {
              const targets = items
                .map((it) => it.prospectId)
                .filter((id) => hasMissingContactFields(id));
              if (targets.length === 0) {
                alert('No missing phone/website fields on this sheet.');
                return;
              }
              setSyncingMissingDetails(true);
              let contactRecoveredCount = 0;
              let secondaryUpdateCount = 0;
              let placeIdRecoveredCount = 0;
              try {
                for (const prospectId of targets) {
                  const updated = await syncProspectDetails(prospectId);
                  if (updated.meaningfulImprovement) contactRecoveredCount += 1;
                  else if (updated.addressImproved || updated.placeIdRecovered) secondaryUpdateCount += 1;
                  if (updated.placeIdRecovered) placeIdRecoveredCount += 1;
                }
              } finally {
                setSyncingMissingDetails(false);
              }
              alert(`Recovered phone or website on ${contactRecoveredCount} of ${targets.length} rows. Secondary address/place updates: ${secondaryUpdateCount}. Place IDs recovered: ${placeIdRecoveredCount}.`);
            }}
            disabled={syncingMissingDetails || items.length === 0}
          >
            {syncingMissingDetails ? 'Syncing...' : `Fill Missing Details (${missingContactCount})`}
          </button>
          <button
            style={{ ...btnStyle, borderColor: '#93c5fd' }}
            onClick={() => {
              if (!nextUncalled) {
                alert('No uncalled prospects left on this sheet.');
                return;
              }
              rowRefs.current[nextUncalled.id]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }}
          >
            Next uncalled
          </button>
          <button
            style={{ ...btnStyle, borderColor: '#93c5fd' }}
            onClick={() => void carryUncalledToNextWeek()}
            disabled={carryingForward || uncalledCount === 0}
          >
            {carryingForward ? 'Carrying...' : `Carry uncalled to next week (${uncalledCount})`}
          </button>
        </div>
      </div>

      <div style={styles.metricsRow}>
        <MetricCard label="Queued" value={queuedCount} />
        <MetricCard label="Done" value={completedCount} />
        <MetricCard label="Uncalled left" value={uncalledCount} />
        <MetricCard label="Logged calls" value={calledCount} />
        <MetricCard label="Callbacks" value={callbackCount} />
        <MetricCard label="Appointments" value={appointmentCount} />
      </div>

      {aiSequence ? (
        <div
          style={{
            marginTop: 10,
            padding: '10px 12px',
            border: '1px solid #dbeafe',
            background: '#f8fbff',
            borderRadius: 10,
            fontSize: 12,
            color: '#1f2937'
          }}
        >
          <strong>
            AI Queue {aiSequence.active ? 'running' : aiSequence.stopRequested ? 'stopped' : 'finished'}
          </strong>
          {`: ${aiSequence.completed} completed, ${aiSequence.failed} failed`}
          {aiSequence.skipped ? `, ${aiSequence.skipped} skipped` : ''}
          {` out of ${aiSequence.total}.`}
          {aiSequenceCurrentLabel ? ` Current: ${aiSequenceCurrentLabel}.` : ''}
          {aiSequence.stopRequested && aiSequence.active ? ' Queue will stop after the current call ends.' : ''}
          {aiSequence.lastError ? ` Last issue: ${aiSequence.lastError}` : ''}
        </div>
      ) : null}

      {manualSequence ? (
        <div
          style={{
            marginTop: 10,
            padding: '10px 12px',
            border: '1px solid #dbeafe',
            background: '#f8fbff',
            borderRadius: 10,
            fontSize: 12,
            color: '#1f2937'
          }}
        >
          <strong>
            Manual Queue {manualSequence.active ? 'running' : manualSequence.stopRequested ? 'stopped' : 'finished'}
          </strong>
          {`: ${manualSequence.completed} completed, ${manualSequence.failed} failed`}
          {manualSequence.skipped ? `, ${manualSequence.skipped} skipped` : ''}
          {` out of ${manualSequence.total}.`}
          {manualSequenceCurrentLabel ? ` Current: ${manualSequenceCurrentLabel}.` : ''}
          {manualSequence.stopRequested && manualSequence.active ? ' Queue will stop after the current call ends.' : ''}
          {manualSequence.lastError ? ` Last issue: ${manualSequence.lastError}` : ''}
        </div>
      ) : null}

      {manualVoiceState.status !== 'idle' && manualVoiceState.status !== 'ready' ? (
        <div
          style={{
            marginTop: 10,
            padding: '10px 12px',
            border: '1px solid #bfdbfe',
            background: '#eff6ff',
            borderRadius: 10,
            fontSize: 12,
            color: '#1e3a8a'
          }}
        >
          <strong>Browser call status:</strong> {manualVoiceState.status}
          {manualVoiceState.prospectId
            ? ` • ${prospectMap[manualVoiceState.prospectId]?.name || getFallbackFromSaved(manualVoiceState.prospectId)?.name || manualVoiceState.prospectId}`
            : ''}
          {manualVoiceState.error ? ` • ${manualVoiceState.error}` : ''}
        </div>
      ) : null}

      <div
        style={{
          marginTop: 10,
          padding: '10px 12px',
          border: '1px solid #e5e7eb',
          borderRadius: 10,
          background: '#fff'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <div>
            <strong>Callbacks due</strong>
            <div style={{ marginTop: 4, fontSize: 12, color: '#6b7280' }}>
              Callback prospects due across all sheets.
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button style={btnStyle} onClick={() => void refreshDueCallbacks()}>
              Refresh
            </button>
            <button
              style={{ ...btnStyle, borderColor: '#93c5fd' }}
              onClick={() => void addAllDueCallbacksToCurrentSheet()}
              disabled={!activeSheet || addingAllDueCallbacks || dueCallbacksNotInSheet.length === 0}
            >
              {addingAllDueCallbacks
                ? 'Adding callbacks...'
                : `Add all due to this week (${dueCallbacksNotInSheet.length})`}
            </button>
          </div>
        </div>
        {loadingDueCallbacks ? (
          <div style={{ marginTop: 8, color: '#6b7280', fontSize: 12 }}>Loading callbacks...</div>
        ) : dueCallbacks.length === 0 ? (
          <div style={{ marginTop: 8, color: '#6b7280', fontSize: 12 }}>No due callbacks right now.</div>
        ) : (
          <div style={{ marginTop: 8, display: 'grid', gap: 8 }}>
            {dueCallbacks.slice(0, 8).map((prospect) => {
              const inSheet = items.some((it) => it.prospectId === prospect.id);
              const callbackPhone =
                bestContact(prospect.id)?.phone ||
                prospect.phone ||
                getFallbackFromSaved(prospect.id)?.phone ||
                '';
              return (
                <div
                  key={prospect.id}
                  style={{
                    border: '1px solid #f3f4f6',
                    borderRadius: 8,
                    padding: 8,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 8,
                    flexWrap: 'wrap'
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 600 }}>{prospect.name || prospect.id}</div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>
                      {prospect.nextReminderAt ? formatMdyTime(new Date(prospect.nextReminderAt)) : 'Due now'}
                      {callbackPhone ? ` • ${callbackPhone}` : ''}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {inSheet ? (
                      <span style={{ fontSize: 12, color: '#1d4ed8', background: '#eff6ff', borderRadius: 999, padding: '4px 8px' }}>
                        On this sheet
                      </span>
                    ) : null}
                    <button
                      style={{ ...btnStyle, borderColor: '#93c5fd' }}
                      onClick={() => void addDueCallbackToCurrentSheet(prospect.id)}
                      disabled={!activeSheet || inSheet || !!addingDueCallbackIds[prospect.id]}
                    >
                      {addingDueCallbackIds[prospect.id] ? 'Adding...' : 'Add to this week'}
                    </button>
                    <button
                      style={{ ...btnStyle, borderColor: '#fecaca', color: '#991b1b' }}
                      onClick={() => void removeDueCallback(prospect.id)}
                      disabled={!!removingDueCallbackIds[prospect.id]}
                    >
                      {removingDueCallbackIds[prospect.id] ? 'Removing...' : 'Remove callback'}
                    </button>
                    <button style={btnStyle} onClick={() => setOpenProspectId(prospect.id)}>
                      Contacts / CRM
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {[
          { id: 'all', label: 'All' },
          { id: 'uncalled', label: 'Uncalled' },
          { id: 'called', label: 'Called' },
          { id: 'callback', label: 'Callbacks' },
          { id: 'appointment', label: 'Appointments' },
          { id: 'rejected', label: 'Rejected' }
        ].map((f) => (
          <button
            key={f.id}
            style={{
              ...btnStyle,
              borderColor: rowFilter === (f.id as any) ? '#93c5fd' : '#e5e7eb',
              background: rowFilter === (f.id as any) ? '#eff6ff' : '#fff'
            }}
            onClick={() => setRowFilter(f.id as any)}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div
        style={{
          ...styles.mainGrid,
          gridTemplateColumns:
            showQuickAddPanel && !isCompact ? 'minmax(0, 2fr) minmax(280px, 1fr)' : 'minmax(0, 1fr)'
        }}
      >
        <section style={styles.queuePanel}>
          {loading && <div style={{ color: '#6b7280' }}>Loading…</div>}

          {!loading && items.length === 0 && (
            <div style={styles.emptyState}>
              <strong>No prospects in this call sheet yet.</strong>
              <div style={{ marginTop: 6, fontSize: 12, color: '#6b7280' }}>
                Use Quick Add from Saved on the right to build your queue.
              </div>
            </div>
          )}

          {!loading && filteredItems.length > 0 && (
            <div style={{ display: 'grid', gap: 10 }}>
              {filteredItems.map((it) => {
                const p = prospectMap[it.prospectId];
                const fallback = getFallbackFromSaved(it.prospectId);
                const contacts = contactsMap[it.prospectId] || [];
                const contact = bestContact(it.prospectId);
                const dmContact = contacts.find((c) => c.isDecisionMaker);
                const dmNamedContact = contacts.find((c) => c.isDecisionMaker && !!c.name?.trim());
                const anyNamedContact = contacts.find((c) => !!c.name?.trim());
                const displayNameContact = dmNamedContact || (!dmContact ? anyNamedContact : null);
                const displayContactName = displayNameContact?.name?.trim() || p?.ownerName?.trim() || '';
                const displayContactRole = displayNameContact?.role
                  ? displayNameContact.role.replace('_', ' ')
                  : (p?.ownerName ? 'owner' : '');
                const displayContactEmail = displayNameContact?.email || p?.ownerEmail || '';
                const phone = contact?.phone || p?.phone || fallback?.phone;
                const website = p?.website || fallback?.website;
                const detailSyncResult = detailSyncResultByProspect[it.prospectId];
                const label = p?.name || fallback?.name || it.prospectId;
                const category = p?.categoryLabel || fallback?.category || 'Business';
                const lastOutcome = it.lastOutcome;
                const queueState = queueStateFromOutcome(lastOutcome);
                const latestOutbound = latestOutboundByProspect[it.prospectId];
                const manualCallActiveForRow =
                  manualVoiceState.prospectId === it.prospectId &&
                  (manualVoiceState.status === 'connecting' ||
                    manualVoiceState.status === 'connected' ||
                    manualVoiceState.status === 'disconnecting');
                const rowFinalAtMs = new Date(it.lastCalledAt || p?.lastOutcomeAt || 0).getTime();
                const outboundStartMs = new Date(latestOutbound?.startedAt || latestOutbound?.createdAt || 0).getTime();
                const rowAlreadySettledCurrentCall =
                  !!latestOutbound &&
                  (latestOutbound.status === 'dialing' || latestOutbound.status === 'in_progress') &&
                  rowFinalAtMs > 0 &&
                  outboundStartMs > 0 &&
                  rowFinalAtMs >= outboundStartMs;
                const qualityScore = p?.targetQualityScore;
                const qualityText = qualityScore ? `${QUALITY_LABELS[qualityScore]} (${qualityScore}/5)` : 'Not rated';
                const appointmentAt =
                  queueState === 'appointment' && p?.nextAppointmentAt
                    ? p.nextAppointmentAt
                    : undefined;
                const statusMeta = {
                  uncalled: { text: '○ Uncalled', short: 'UC', color: '#6b7280', bg: '#f3f4f6' },
                  called: { text: '✓ Called', short: 'CL', color: '#1d4ed8', bg: '#eff6ff' },
                  callback: { text: '↻ Callback', short: 'CB', color: '#92400e', bg: '#fef3c7' },
                  rejected: { text: '✕ Rejected', short: 'RJ', color: '#991b1b', bg: '#fee2e2' },
                  appointment: { text: '📅 Appointment', short: 'AP', color: '#065f46', bg: '#d1fae5' }
                }[queueState];
                const callPill =
                  manualCallActiveForRow
                    ? {
                        text:
                          manualVoiceState.status === 'connected'
                            ? 'Manual Call Live'
                            : manualVoiceState.status === 'disconnecting'
                              ? 'Ending Manual Call'
                              : 'Dialing Manual Call',
                        color: '#0c4a6e',
                        bg: '#e0f2fe'
                      }
                    : (latestOutbound?.status === 'in_progress' || latestOutbound?.status === 'dialing') && !rowAlreadySettledCurrentCall
                    ? {
                        text:
                          latestOutbound.callMode === 'manual'
                            ? latestOutbound.status === 'dialing'
                              ? 'Dialing Manual Call'
                              : 'Manual Call Live'
                            : latestOutbound.status === 'dialing'
                              ? 'Dialing AI Call'
                              : 'AI Call Live',
                        color: '#0c4a6e',
                        bg: '#e0f2fe'
                      }
                    : latestOutbound?.status === 'failed'
                      ? { text: 'Call failed', color: '#991b1b', bg: '#fee2e2' }
                      : null;
                const isCurrent = nextUncalled?.id === it.id;

                return (
                  <article
                    key={it.id}
                    ref={(el) => {
                      rowRefs.current[it.id] = el;
                    }}
                    style={{
                      ...styles.queueRow,
                      borderColor: isCurrent ? '#93c5fd' : '#e5e7eb',
                      gridTemplateColumns: isCompact
                        ? 'minmax(0, 1fr)'
                        : 'minmax(0, 2.1fr) minmax(0, 1.6fr) minmax(0, 2fr) minmax(0, 2.5fr)'
                    }}
                  >
                    <div style={styles.prospectCell}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <strong>{label}</strong>
                        <span style={{ fontSize: 12, color: '#6b7280' }}>{category}</span>
                        {contact?.isDecisionMaker && <span style={styles.tag}>DM</span>}
                        <span style={{ fontSize: 11, color: statusMeta.color, background: statusMeta.bg, borderRadius: 999, padding: '2px 8px' }}>
                          {statusMeta.short}
                        </span>
                        {isCurrent ? <span style={styles.tag}>Next</span> : null}
                      </div>
                      <div style={{ marginTop: 4, fontSize: 12, color: '#6b7280' }}>{p?.address || fallback?.address || 'Address N/A'}</div>
                      <div style={{ marginTop: 4, fontSize: 12, color: '#374151' }}>
                        Target quality: {qualityText}
                      </div>
                      <div style={{ marginTop: 4, fontSize: 12, color: statusMeta.color }}>
                        {statusMeta.text}
                      </div>
                      {appointmentAt ? (
                        <div style={{ marginTop: 4, fontSize: 12, color: '#065f46' }}>
                          Appointment: {formatMdyTime(new Date(appointmentAt))}
                        </div>
                      ) : null}
                      {callPill ? (
                        <div style={{ marginTop: 4, fontSize: 11, color: callPill.color, background: callPill.bg, borderRadius: 999, padding: '2px 8px', display: 'inline-block' }}>
                          {callPill.text}
                        </div>
                      ) : null}
                    </div>

                    <div
                      style={{
                        ...styles.contactCell,
                        borderLeft: isCompact ? 'none' : styles.contactCell.borderLeft,
                        paddingLeft: isCompact ? 0 : styles.contactCell.paddingLeft
                      }}
                    >
                      {displayContactName ? (
                        <div style={{ fontSize: 12 }}>
                          <strong>{displayContactName}</strong>
                          {displayNameContact?.isDecisionMaker ? ' (DM)' : ''}
                          {displayContactRole ? ` • ${displayContactRole}` : ''}
                        </div>
                      ) : null}
                      {displayContactEmail ? (
                        <div style={{ fontSize: 12, color: '#6b7280' }}>{displayContactEmail}</div>
                      ) : null}
                      <div style={{ fontSize: 12, color: '#374151' }}>{phone || 'Phone N/A'}</div>
                      <div style={{ fontSize: 12, color: '#6b7280' }}>{websiteDisplayText(website)}</div>
                    </div>

                    <div
                      style={{
                        ...styles.actionsCell,
                        borderLeft: isCompact ? 'none' : styles.actionsCell.borderLeft,
                        paddingLeft: isCompact ? 0 : styles.actionsCell.paddingLeft
                      }}
                    >
                      <button
                        onClick={() => void startAiCallNow(it.prospectId)}
                        style={{ ...btnStyle, borderColor: '#93c5fd' }}
                        disabled={
                          !!startingAiCallByProspect[it.prospectId] ||
                          !!endingAiCallByProspect[it.prospectId] ||
                          manualCallActiveForRow ||
                          (!!manualSequence?.active && manualSequence.currentProspectId !== it.prospectId) ||
                          (!!aiSequence?.active && aiSequence.currentProspectId !== it.prospectId) ||
                          !!p?.dnc ||
                          !phone
                        }
                        title={p?.dnc ? 'Prospect is marked DNC' : (!phone ? 'Missing phone number' : 'Start AI outbound call')}
                      >
                        {startingAiCallByProspect[it.prospectId] ? 'Starting...' : 'AI Call Now'}
                      </button>
                      <button
                        onClick={() => void startManualCallNow(it.prospectId)}
                        style={{ ...btnStyle, borderColor: '#86efac' }}
                        disabled={
                          !!startingManualCallByProspect[it.prospectId] ||
                          !!endingManualCallByProspect[it.prospectId] ||
                          (!!aiSequence?.active && aiSequence.currentProspectId !== it.prospectId) ||
                          (!!manualSequence?.active && manualSequence.currentProspectId !== it.prospectId) ||
                          (!!manualVoiceState.prospectId && manualVoiceState.prospectId !== it.prospectId && manualVoiceState.status !== 'ready' && manualVoiceState.status !== 'idle') ||
                          !!p?.dnc ||
                          !phone
                        }
                        title={p?.dnc ? 'Prospect is marked DNC' : (!phone ? 'Missing phone number' : 'Start manual browser call')}
                      >
                        {startingManualCallByProspect[it.prospectId] ? 'Starting...' : 'Manual Call'}
                      </button>
                      {(latestOutbound?.status === 'dialing' || latestOutbound?.status === 'in_progress') &&
                      latestOutbound?.callMode !== 'manual' &&
                      !rowAlreadySettledCurrentCall ? (
                        <button
                          onClick={() => void endAiCallNow(it.prospectId)}
                          style={{ ...btnStyle, borderColor: '#fecaca', color: '#b91c1c' }}
                          disabled={!!endingAiCallByProspect[it.prospectId]}
                        >
                          {endingAiCallByProspect[it.prospectId] ? 'Ending...' : 'End AI Call'}
                        </button>
                      ) : null}
                      {manualCallActiveForRow ? (
                        <button
                          onClick={() => void endManualCallNow(it.prospectId)}
                          style={{ ...btnStyle, borderColor: '#fecaca', color: '#b91c1c' }}
                          disabled={!!endingManualCallByProspect[it.prospectId]}
                        >
                          {endingManualCallByProspect[it.prospectId] ? 'Ending...' : 'End Manual Call'}
                        </button>
                      ) : null}
                      <button onClick={() => void openCall(it.prospectId)} style={btnStyle}>
                        Call
                      </button>
                      <button onClick={() => void openWebsite(it.prospectId)} style={btnStyle}>
                        Website
                      </button>
                      <button onClick={() => openDirections(it.prospectId)} style={btnStyle}>
                        Directions
                      </button>
                      <button
                        onClick={() => void syncProspectDetails(it.prospectId)}
                        style={{ ...btnStyle, borderColor: '#bfdbfe' }}
                        disabled={!!syncingByProspect[it.prospectId]}
                      >
                        {syncingByProspect[it.prospectId] ? 'Syncing...' : 'Sync details'}
                      </button>
                      {detailSyncResult ? (
                        <span
                          style={{
                            fontSize: 11,
                            color: detailSyncResult.color,
                            background: detailSyncResult.background,
                            borderRadius: 999,
                            padding: '4px 8px'
                          }}
                        >
                          {detailSyncResult.label}
                        </span>
                      ) : null}
                      <button
                        onClick={() => void markProspectDnc(it.prospectId)}
                        style={{ ...btnStyle, borderColor: '#fecaca', color: '#b91c1c' }}
                        disabled={!!p?.dnc}
                      >
                        {p?.dnc ? 'DNC' : 'Mark DNC'}
                      </button>
                      <button onClick={() => setOpenProspectId(it.prospectId)} style={{ ...btnStyle, borderColor: '#bfdbfe' }}>
                        Contacts / CRM
                      </button>
                      <button
                        onClick={() => void promptMoveRowToWeek(it)}
                        style={{ ...btnStyle, borderColor: '#c7d2fe' }}
                        disabled={!!movingByItemId[it.id]}
                      >
                        {movingByItemId[it.id] ? 'Moving...' : 'Move to week...'}
                      </button>
                      <button
                        onClick={() => void removeRowFromSheet(it)}
                        style={{ ...btnStyle, borderColor: '#fecaca', color: '#b91c1c' }}
                        disabled={!!removingByItemId[it.id]}
                      >
                        {removingByItemId[it.id] ? 'Removing...' : 'Remove from sheet'}
                      </button>
                    </div>

                    <div
                      style={{
                        ...styles.outcomesCell,
                        borderLeft: isCompact ? 'none' : styles.outcomesCell.borderLeft,
                        paddingLeft: isCompact ? 0 : styles.outcomesCell.paddingLeft
                      }}
                    >
                      <div style={{ marginBottom: 8 }}>
                        <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>Rate target quality</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                          {[1, 2, 3, 4, 5].map((value) => {
                            const score = value as 1 | 2 | 3 | 4 | 5;
                            const active = !!qualityScore && score <= qualityScore;
                            return (
                              <button
                                key={value}
                                onClick={() => void setProspectQuality(it.prospectId, score)}
                                title={`${value} - ${QUALITY_LABELS[score]}`}
                                style={{
                                  ...styles.starBtn,
                                  color: active ? '#f59e0b' : '#d1d5db'
                                }}
                              >
                                ★
                              </button>
                            );
                          })}
                          <span style={{ fontSize: 11, color: '#6b7280', marginLeft: 4 }}>{qualityText}</span>
                        </div>
                      </div>
                      <input
                        style={styles.noteInput}
                        placeholder="Call notes (optional)"
                        value={notesByProspect[it.prospectId] || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          setNotesByProspect((prev) => ({ ...prev, [it.prospectId]: value }));
                        }}
                      />
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {OUTCOMES.map((r) => {
                          const active = lastOutcome === r;
                          return (
                            <button
                              key={r}
                              onClick={() => void handleOutcomeClick(it.prospectId, r)}
                              style={{
                                ...outcomeBtn,
                                background: active ? '#e0f2fe' : '#f9fafb',
                                borderColor: active ? '#7dd3fc' : '#e5e7eb'
                              }}
                            >
                              {r}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}

          {!loading && items.length > 0 && filteredItems.length === 0 && (
            <div style={styles.emptyState}>
              <strong>No prospects match this filter.</strong>
              <div style={{ marginTop: 6, fontSize: 12, color: '#6b7280' }}>
                Try switching the call sheet filter chips above.
              </div>
            </div>
          )}
        </section>

        {showQuickAddPanel && (
          <aside style={styles.quickAddPanel}>
          <h4 style={{ margin: '0 0 8px' }}>Quick Add from Saved</h4>
          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
            {quickAddCandidates.length} saved prospects available for this sheet.
          </div>
          <input
            style={styles.searchInput}
            placeholder="Search saved prospects..."
            value={quickAddQuery}
            onChange={(e) => setQuickAddQuery(e.target.value)}
          />
          <div style={{ marginTop: 10, display: 'grid', gap: 6, maxHeight: 420, overflow: 'auto' }}>
            {quickAddVisible.map((p) => {
              const stableId = toStableId(p);
              const adding = !!addingByStableId[stableId];
              return (
                <div key={p.id} style={styles.quickAddRow}>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600 }}>{p.name}</div>
                    <div style={{ fontSize: 11, color: '#6b7280' }}>{p.category || 'Business'}</div>
                  </div>
                  <button style={btnStyle} onClick={() => void addFromSaved(p)} disabled={adding}>
                    {adding ? 'Adding...' : 'Add'}
                  </button>
                </div>
              );
            })}
          </div>
          {quickAddCandidates.length > 12 && (
            <button style={{ ...btnStyle, width: '100%', marginTop: 10 }} onClick={() => setShowAllQuickAdd((v) => !v)}>
              {showAllQuickAdd ? 'Show fewer' : `Show all (${quickAddCandidates.length})`}
            </button>
          )}
          </aside>
        )}
      </div>

      {openProspectId && (
        <ProspectDetailModal
          currentUser={currentUser}
          prospectId={openProspectId}
          onClose={() => {
            const closedId = openProspectId;
            setOpenProspectId(null);
            if (closedId) void hydrateProspect(closedId, true);
          }}
        />
      )}

      {appointmentDraft && (
        <div style={styles.modalBackdrop} onClick={() => !savingAppointment && setAppointmentDraft(null)}>
          <div style={styles.appointmentModal} onClick={(e) => e.stopPropagation()}>
            <h4 style={{ margin: '0 0 10px' }}>{appointmentDraft.outcome} Details</h4>
            {(() => {
              const { hour, minute } = splitTime(appointmentDraft.time);
              return (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <input
                type="date"
                style={styles.modalInput}
                value={appointmentDraft.date}
                onChange={(e) => setAppointmentDraft((prev) => (prev ? { ...prev, date: e.target.value } : prev))}
              />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <select
                  style={styles.modalInput}
                  value={hour}
                  onChange={(e) => {
                    const nextHour = Number(e.target.value);
                    setAppointmentDraft((prev) => (prev ? { ...prev, time: toTimeString(nextHour, minute) } : prev));
                  }}
                >
                  {hourOptions.map((h) => (
                    <option key={h} value={h}>
                      {new Date(2020, 0, 1, h, 0).toLocaleTimeString('en-US', { hour: 'numeric' })}
                    </option>
                  ))}
                </select>
                <select
                  style={styles.modalInput}
                  value={minute}
                  onChange={(e) => {
                    const nextMinute = Number(e.target.value);
                    setAppointmentDraft((prev) => (prev ? { ...prev, time: toTimeString(hour, nextMinute) } : prev));
                  }}
                >
                  {minuteOptions.map((m) => (
                    <option key={m} value={m}>
                      {String(m).padStart(2, '0')}
                    </option>
                  ))}
                </select>
              </div>
              <input
                type="number"
                min={15}
                step={15}
                style={styles.modalInput}
                placeholder="Duration (min)"
                value={String(appointmentDraft.durationMinutes)}
                onChange={(e) =>
                  setAppointmentDraft((prev) =>
                    prev ? { ...prev, durationMinutes: Math.max(15, Number(e.target.value) || 30) } : prev
                  )
                }
              />
              <select
                style={styles.modalInput}
                value={appointmentDraft.appointmentType}
                onChange={(e) =>
                  setAppointmentDraft((prev) =>
                    prev ? { ...prev, appointmentType: e.target.value as AppointmentType } : prev
                  )
                }
              >
                <option value="in_person">In person</option>
                <option value="phone">Phone</option>
                <option value="virtual">Virtual</option>
              </select>
            </div>
              );
            })()}
            <input
              type="text"
              style={{ ...styles.modalInput, marginTop: 8 }}
              placeholder="Location"
              value={appointmentDraft.appointmentLocation}
              onChange={(e) =>
                setAppointmentDraft((prev) => (prev ? { ...prev, appointmentLocation: e.target.value } : prev))
              }
            />
            <input
              type="text"
              style={{ ...styles.modalInput, marginTop: 8 }}
              placeholder="Contact name"
              value={appointmentDraft.appointmentContactName}
              onChange={(e) =>
                setAppointmentDraft((prev) => (prev ? { ...prev, appointmentContactName: e.target.value } : prev))
              }
            />
            <div style={styles.modalActions}>
              <button style={btnStyle} onClick={() => setAppointmentDraft(null)} disabled={savingAppointment}>
                Cancel
              </button>
              <button style={{ ...btnStyle, borderColor: '#93c5fd' }} onClick={() => void saveAppointmentOutcome()} disabled={savingAppointment}>
                {savingAppointment ? 'Saving...' : 'Save appointment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showSheetPicker && (
        <div
          style={styles.modalBackdrop}
          onClick={() => {
            setShowSheetPicker(false);
            setSheetSearchQuery('');
            setShowAllSheets(false);
          }}
        >
          <div style={styles.sheetPickerModal} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <h4 style={{ margin: 0 }}>Call Sheets</h4>
              <button
                style={btnStyle}
                onClick={() => {
                  setShowSheetPicker(false);
                  setSheetSearchQuery('');
                  setShowAllSheets(false);
                }}
              >
                Close
              </button>
            </div>
            <input
              style={styles.searchInput}
              placeholder="Search by name or date..."
              value={sheetSearchQuery}
              onChange={(e) => setSheetSearchQuery(e.target.value)}
            />
            <div style={{ marginTop: 10, display: 'grid', gap: 8, maxHeight: '60vh', overflow: 'auto' }}>
              {visibleSheets.map((sheet) => {
                const active = activeSheet?.id === sheet.id;
                return (
                  <button
                    key={sheet.id}
                    style={{
                      ...styles.sheetPickerRow,
                      borderColor: active ? '#93c5fd' : '#e5e7eb',
                      background: active ? '#eff6ff' : '#fff'
                    }}
                    onClick={() => {
                      void loadSheet(sheet);
                      setShowSheetPicker(false);
                      setSheetSearchQuery('');
                      setShowAllSheets(false);
                    }}
                  >
                    <strong style={{ fontSize: 13 }}>{formatSheetDisplayName(sheet, todayKey, currentWeekStartKey)}</strong>
                    <span style={{ fontSize: 11, color: '#6b7280' }}>
                      {isAutoCallSheet(sheet) ? 'Auto call sheet' : 'Custom sheet'}
                    </span>
                  </button>
                );
              })}
              {visibleSheets.length === 0 ? (
                <div style={styles.emptyState}>
                  <strong>No call sheets match that search.</strong>
                </div>
              ) : null}
            </div>
            {hiddenSheetCount > 0 && !sheetSearchQuery.trim() ? (
              <button
                style={{ ...btnStyle, width: '100%', marginTop: 10 }}
                onClick={() => setShowAllSheets(true)}
              >
                Show {hiddenSheetCount} older sheets
              </button>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}

function MetricCard(props: { label: string; value: number }) {
  const { label, value } = props;
  return (
    <div style={styles.metricCard}>
      <div style={{ fontSize: 11, color: '#6b7280' }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700 }}>{value}</div>
    </div>
  );
}

const btnStyle: React.CSSProperties = {
  padding: '8px 10px',
  borderRadius: 10,
  border: '1px solid #e5e7eb',
  background: '#fff',
  cursor: 'pointer',
  fontSize: 12
};

const outcomeBtn: React.CSSProperties = {
  padding: '7px 9px',
  borderRadius: 999,
  border: '1px solid #e5e7eb',
  background: '#f9fafb',
  cursor: 'pointer',
  fontSize: 11
};

const styles: Record<string, React.CSSProperties> = {
  page: { padding: 12 },
  topBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    flexWrap: 'wrap'
  },
  select: {
    padding: '8px 10px',
    borderRadius: 8,
    border: '1px solid #e5e7eb'
  },
  metricsRow: {
    marginTop: 10,
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))',
    gap: 8
  },
  metricCard: {
    border: '1px solid #e5e7eb',
    borderRadius: 10,
    background: '#fff',
    padding: '8px 10px'
  },
  mainGrid: {
    marginTop: 12,
    display: 'grid',
    gap: 12
  },
  queuePanel: {
    border: '1px solid #e5e7eb',
    borderRadius: 12,
    background: '#fff',
    padding: 10,
    minWidth: 0
  },
  quickAddPanel: {
    border: '1px solid #e5e7eb',
    borderRadius: 12,
    background: '#fff',
    padding: 10,
    alignSelf: 'start',
    minWidth: 0
  },
  searchInput: {
    width: '100%',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    padding: '8px 10px',
    fontSize: 12
  },
  quickAddRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    border: '1px solid #f3f4f6',
    borderRadius: 8,
    padding: 8
  },
  emptyState: {
    border: '1px dashed #d1d5db',
    borderRadius: 12,
    padding: 12,
    background: '#fafafa'
  },
  queueRow: {
    border: '1px solid #e5e7eb',
    borderRadius: 10,
    padding: 10,
    display: 'grid',
    gap: 10,
    alignItems: 'start',
    minWidth: 0
  },
  prospectCell: { minWidth: 0, overflowWrap: 'anywhere' },
  contactCell: {
    minWidth: 0,
    borderLeft: '1px solid #f3f4f6',
    paddingLeft: 10,
    overflowWrap: 'anywhere'
  },
  actionsCell: {
    minWidth: 0,
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
    borderLeft: '1px solid #f3f4f6',
    paddingLeft: 10
  },
  outcomesCell: {
    minWidth: 0,
    borderLeft: '1px solid #f3f4f6',
    paddingLeft: 10
  },
  noteInput: {
    width: '100%',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    padding: '8px 10px',
    fontSize: 12,
    marginBottom: 8
  },
  modalBackdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.4)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 2100,
    padding: 12
  },
  appointmentModal: {
    width: 'min(560px, 96vw)',
    background: '#fff',
    borderRadius: 12,
    border: '1px solid #e5e7eb',
    padding: 12
  },
  sheetPickerModal: {
    width: 'min(620px, 96vw)',
    background: '#fff',
    borderRadius: 12,
    border: '1px solid #e5e7eb',
    padding: 12
  },
  modalInput: {
    width: '100%',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    padding: '8px 10px',
    fontSize: 12
  },
  modalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
    marginTop: 10
  },
  tag: {
    fontSize: 11,
    border: '1px solid #93c5fd',
    color: '#1d4ed8',
    padding: '1px 6px',
    borderRadius: 999
  },
  starBtn: {
    border: 'none',
    background: 'transparent',
    padding: '2px 1px',
    fontSize: 18,
    lineHeight: 1,
    cursor: 'pointer'
  },
  sheetPickerRow: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 2,
    width: '100%',
    border: '1px solid #e5e7eb',
    borderRadius: 10,
    padding: '10px 12px',
    cursor: 'pointer'
  }
};

